import { ApiHttpError } from "./errors.js";
/** A unidade pedida, sob qualquer um dos dois nomes. Um lugar so'. */
const idDaUnidade = (opts) => opts?.unidadeId ?? opts?.prefeituraId;
const queryDaUnidade = (opts, extra) => Object.fromEntries(Object.entries({
    ...(extra ?? {}),
    ...(idDaUnidade(opts) != null ? { unidadeId: idDaUnidade(opts) } : {}),
})
    // `undefined` e string vazia saem: um `?competencia=` vazio na URL nao e'
    // o mesmo que a ausencia do filtro, e a API trataria como pedido.
    .filter(([, v]) => v !== undefined && v !== ""));
/** O sufixo `?unidadeId=` para as rotas que devolvem URL em vez de dados. */
const sufixoDaUnidade = (opts, jaTemQuery = false) => idDaUnidade(opts) != null ? `${jaTemQuery ? "&" : "?"}unidadeId=${idDaUnidade(opts)}` : "";
/**
 * O CORPO DO ENSAIO — multipart quando são arquivos soltos, corpo cru quando é
 * um pacote só.
 *
 * O NOME DE CADA ARQUIVO É DADO, e não enfeite: metade do catálogo reconhece
 * pelo nome (`ALECE-SERVIDOR-2609.TXT`, `carga202609.csv`). Um `File` solto
 * mandado como corpo cru chegaria sem nome nenhum — a API leria "arquivo" e o
 * layout que reconhece por nome diria que não é dele. Por isso qualquer coisa
 * com `name` vira `multipart/form-data`, que é o único envelope que carrega o
 * nome junto; o `Blob` sem nome (o zip de exemplo, por exemplo) vai cru,
 * porque o zip já traz os nomes das entradas dentro dele.
 *
 * A forma é estrutural (`typeof x.name === "string"`) e não `instanceof File`:
 * o `File` global não existe em todo runtime que usa este SDK.
 */
function corpoDoEnsaio(arquivos) {
    if (typeof FormData !== "undefined" && arquivos instanceof FormData)
        return arquivos;
    if (Array.isArray(arquivos)) {
        const fd = new FormData();
        for (const f of arquivos)
            fd.append("arquivo", f, f.name);
        return fd;
    }
    const solto = arquivos;
    if (typeof solto.name === "string" && solto.name) {
        const fd = new FormData();
        fd.append("arquivo", solto, solto.name);
        return fd;
    }
    return arquivos;
}
/**
 * Extrai os dois lados de uma recusa por reconhecimento, ou `null` se o erro
 * for de outra natureza. Nunca lança — um erro ao explicar um erro seria pior
 * que a mensagem genérica que isto substitui.
 */
export function lerReconhecimentoRecusado(e) {
    const d = e?.details;
    if (!d?.esperado || !d?.encontrado)
        return null;
    try {
        return {
            esperado: JSON.parse(d.esperado),
            encontrado: JSON.parse(d.encontrado),
        };
    }
    catch {
        return null;
    }
}
/** Minimal in-memory storage used as default. Replace in app code with SecureStore / localStorage. */
class MemoryStorage {
    access = null;
    refresh = null;
    getAccess() {
        return this.access;
    }
    getRefresh() {
        return this.refresh;
    }
    set(t) {
        this.access = t.access_token;
        this.refresh = t.refresh_token;
    }
    clear() {
        this.access = null;
        this.refresh = null;
    }
}
/** O pedido de repasse (#279) em multipart: o comprovante e' arquivo. */
function formDoRepasse(p) {
    const fd = new FormData();
    fd.append("credorId", String(p.credorId));
    fd.append("valorCentavos", String(p.valorCentavos));
    fd.append("dataRepasse", p.dataRepasse);
    if (p.observacao)
        fd.append("observacao", p.observacao);
    if (p.comprovante)
        fd.append("comprovante", p.comprovante, p.comprovante.name ?? "comprovante");
    return fd;
}
export class AtlasClient {
    opts;
    gruposDesconto = {
        listar: (folhaId) => this.request(`/v1/admin/folhas/${encodeURIComponent(folhaId)}/grupos-desconto`),
        detalhe: (folhaId, grupoId) => this.request(`/v1/admin/folhas/${encodeURIComponent(folhaId)}/grupos-desconto/${encodeURIComponent(grupoId)}`),
        reavaliar: (folhaId, grupoId, versao) => this.request(`/v1/admin/folhas/${encodeURIComponent(folhaId)}/grupos-desconto/${encodeURIComponent(grupoId)}/reavaliar`, { method: "POST", body: { versao } }),
        confirmar: (folhaId, grupoId, body) => this.request(`/v1/admin/folhas/${encodeURIComponent(folhaId)}/grupos-desconto/${encodeURIComponent(grupoId)}/confirmar`, { method: "POST", body }),
    };
    fetchImpl;
    storage;
    refreshing = null;
    constructor(opts) {
        this.opts = opts;
        this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
        this.storage = opts.storage ?? new MemoryStorage();
    }
    // ============ Auth ============
    /** Login. Se o usuario tem 2FA ativo, resposta e `{ requires_2fa: true, mfa_token }`
     *  — nenhum token e persistido no storage. O caller precisa chamar verify2fa() com
     *  o mfa_token e o codigo TOTP pra concluir o login. */
    async login(req) {
        const res = await this.request("/v1/auth/login", { method: "POST", body: req, skipAuth: true });
        if ("requires_2fa" in res && res.requires_2fa) {
            // Nao persiste — o access_token ainda nao existe.
            return res;
        }
        const ok = res;
        await this.storage.set({
            access_token: ok.access_token,
            refresh_token: ok.refresh_token,
            expires_in: ok.expires_in,
        });
        return ok;
    }
    /** Conclui o login apos o passo de 2FA — troca (mfa_token, code) por access+refresh. */
    async verify2fa(mfa_token, code) {
        const res = await this.request("/v1/auth/verify-2fa", {
            method: "POST",
            body: { mfa_token, code },
            skipAuth: true,
        });
        await this.storage.set({
            access_token: res.access_token,
            refresh_token: res.refresh_token,
            expires_in: res.expires_in,
        });
        return res;
    }
    /** Self-service do usuario autenticado. Cobre 2FA hoje. */
    me = {
        twoFactor: {
            /** Status atual — { enabled, account }. */
            status: () => this.request("/v1/me/2fa"),
            /** Comeca setup — gera secret novo (nao persiste) + otpauth pro QR. */
            setup: () => this.request("/v1/me/2fa/setup", { method: "POST" }),
            /** Confirma o setup com o codigo TOTP — so aqui persiste. */
            confirm: (code) => this.request("/v1/me/2fa/confirm", { method: "POST", body: { code } }),
            /** Desativa 2FA (precisa do codigo atual). */
            disable: (code) => this.request("/v1/me/2fa/disable", { method: "POST", body: { code } }),
        },
    };
    // ===== Recuperar senha do servidor (fluxo antigo, CPF-only — mantido pra retrocompat) =====
    esqueciSenha = {
        solicitar: (cpf) => this.request("/v1/auth/esqueci-senha/solicitar", { method: "POST", body: { cpf }, skipAuth: true }),
        redefinir: (cpf, codigo, senha) => this.request("/v1/auth/esqueci-senha/redefinir", {
            method: "POST",
            body: { cpf, codigo, senha },
            skipAuth: true,
        }),
        /** Universal: aceita CPF (servidor) ou e-mail (banco/prefeitura/averbadora).
         *
         *  NAO devolve `perfil` (removido em 09/09/2026): ele so aparecia para
         *  identificador EXISTENTE, entao a presenca do campo respondia "esta conta
         *  existe" e ainda dizia qual dos quatro perfis. O `universalRedefinir`
         *  re-resolve o perfil no servidor e devolve o dele — nunca dependeu deste. */
        universalSolicitar: (identifier) => this.request("/v1/auth/esqueci-senha/universal-solicitar", {
            method: "POST",
            body: { identifier },
            skipAuth: true,
        }),
        universalRedefinir: (identifier, codigo, senha) => this.request("/v1/auth/esqueci-senha/universal-redefinir", { method: "POST", body: { identifier, codigo, senha }, skipAuth: true }),
    };
    // ===== Primeiro acesso do servidor (ativa a conta a partir do cadastro da prefeitura) =====
    primeiroAcesso = {
        /** Rota PUBLICA, chaveada por CPF. Devolve o minimo para a pessoa se
         *  reconhecer antes de escolher e-mail e senha — e nada alem disso.
         *
         *  `email_masked` e `telefone_masked` SAIRAM em 09/09/2026: nao tinham
         *  consumidor, e as mascaras entregavam o dominio do e-mail (isto e', o
         *  orgao) e os quatro ultimos digitos do telefone a quem informasse um CPF. */
        buscar: (cpf) => this.request("/v1/auth/primeiro-acesso/buscar", { method: "POST", body: { cpf }, skipAuth: true }),
        /** Servidor escolhe email + senha + telefone; codigo vai pra esse email. */
        codigo: (cpf, email, senha, telefone) => this.request("/v1/auth/primeiro-acesso/codigo", { method: "POST", body: { cpf, email, senha, telefone }, skipAuth: true }),
        /** Confirma o codigo — grava passwordHash + o novo email no servidor. */
        confirmar: (cpf, codigo) => this.request("/v1/auth/primeiro-acesso/senha", {
            method: "POST",
            body: { cpf, codigo },
            skipAuth: true,
        }),
        /** Master de consignataria ou de organizacao: identifica-se pelo e-mail de
         *  login que a averbadora cadastrou, e o codigo vai para esse mesmo e-mail.
         *  Nao ha senha cadastrada pelo admin — ela nasce aqui. */
        master: {
            buscar: (email) => this.request("/v1/auth/primeiro-acesso/master/buscar", { method: "POST", body: { email }, skipAuth: true }),
            codigo: (email, senha) => this.request("/v1/auth/primeiro-acesso/master/codigo", { method: "POST", body: { email, senha }, skipAuth: true }),
            confirmar: (email, codigo) => this.request("/v1/auth/primeiro-acesso/master/senha", {
                method: "POST",
                body: { email, codigo },
                skipAuth: true,
            }),
        },
    };
    async logout() {
        try {
            await this.request("/v1/auth/logout", { method: "POST" });
        }
        finally {
            await this.storage.clear();
        }
    }
    /**
     * Troca o access token por um novo AGORA, sem esperar ele expirar.
     *
     * O refresh automatico so' dispara em 401, e ha' mudancas que nao invalidam o
     * token — apenas o deixam DESATUALIZADO. O caso que obrigou isto a existir:
     * o operador do banco troca a senha temporaria, o registro passa a
     * `senhaTemporaria: false`, mas o token em maos continua dizendo `true`. O
     * layout le a claim, conclui que a troca ainda falta e devolve a pessoa para a
     * mesma tela — que foi exatamente o "tive que trocar a senha duas vezes".
     *
     * `/v1/auth/refresh` RE-DERIVA as claims do cadastro atual (permissoes e a
     * marca de senha temporaria), entao uma ida ali basta. Devolve `false` quando
     * nao ha sessao para renovar — o chamador decide se isso e' problema.
     */
    async renovarSessao() {
        const refresh = await this.storage.getRefresh();
        if (!refresh)
            return false;
        try {
            const res = await this.request("/v1/auth/refresh", { method: "POST", body: { refresh_token: refresh }, skipAuth: true });
            await this.storage.set(res);
            return true;
        }
        catch {
            return false;
        }
    }
    // ============ Servidores ============
    async getMyProfile() {
        return this.request("/v1/servidores/me");
    }
    async getMyMargem(opts) {
        return this.request("/v1/servidores/me/margem-consignavel", {
            query: {
                force_refresh: opts?.forceRefresh,
                include: opts?.include?.join(","),
            },
        });
    }
    /** All matrículas of the logged-in servidor, each with full data (margem, contratos, elegíveis). */
    async getMyMatriculas() {
        return this.request("/v1/servidores/me/matriculas");
    }
    // ============ Confirmação por código (step-up, qualquer persona) ============
    // Envia um código de 6 dígitos para o e-mail cadastrado de quem está operando
    // e devolve o destino para o front avisar "código enviado para <e-mail>".
    confirmacao = {
        solicitar: (acao, recurso) => this.request("/v1/confirmacao/solicitar", {
            method: "POST",
            body: { acao, recurso },
        }),
        verificar: (challengeId, codigo) => this.request("/v1/confirmacao/verificar", {
            method: "POST",
            body: { challengeId, codigo },
        }),
    };
    // ============ Portabilidade (rito proponente/credora) ============
    portabilidade = {
        // === Servidor ===
        elegiveis: (matricula) => this.request("/v1/servidores/me/portabilidade/elegiveis", { query: matricula ? { matricula } : {} }),
        minhas: (estado) => this.request("/v1/servidores/me/portabilidade", {
            query: estado ? { estado } : {},
        }),
        minha: (id) => this.request(`/v1/servidores/me/portabilidade/${id}`),
        pedir: (body) => this.request("/v1/servidores/me/portabilidade", { method: "POST", body }),
        confirmar: (id, senha, versao) => this.request(`/v1/servidores/me/portabilidade/${id}/confirmar`, {
            method: "POST",
            body: { senha, versao },
        }),
        cancelarComoServidor: (id, motivo) => this.request(`/v1/servidores/me/portabilidade/${id}/cancelar`, {
            method: "POST",
            body: motivo ? { motivo } : {},
        }),
        // === Banco (proponente e credora) ===
        elegiveisBanco: (busca) => this.request("/v1/portal/banco/portabilidade/elegiveis", { query: busca }),
        doBanco: (q) => this.request("/v1/portal/banco/portabilidade", { query: q ?? {} }),
        doBancoDetalhe: (id) => this.request(`/v1/portal/banco/portabilidade/${id}`),
        abrir: (adf, condicoes) => this.request("/v1/portal/banco/portabilidade", { method: "POST", body: { adf, condicoes } }),
        informarSaldo: (id, saldoDevedor) => this.request(`/v1/portal/banco/portabilidade/${id}/saldo`, { method: "POST", body: { saldoDevedor } }),
        decidirComoCredora: (id, body) => this.request(`/v1/portal/banco/portabilidade/${id}/decisao`, { method: "POST", body }),
        informarCondicoes: (id, body) => this.request(`/v1/portal/banco/portabilidade/${id}/proposta`, { method: "POST", body }),
        comprovanteUrl: (id) => `${this.opts.baseUrl}/v1/portal/banco/portabilidade/${id}/comprovante`,
        informarPagamento: (id, pagamento) => {
            if (!pagamento.comprovante)
                return this.request(`/v1/portal/banco/portabilidade/${id}/pagamento`, { method: "POST", body: { valorPago: pagamento.valorPago } });
            const body = new FormData();
            body.append("valorPago", String(pagamento.valorPago));
            body.append("comprovante", pagamento.comprovante);
            return this.request(`/v1/portal/banco/portabilidade/${id}/pagamento`, { method: "POST", body, isFormData: true });
        },
        liquidar: (id) => this.request(`/v1/portal/banco/portabilidade/${id}/liquidar`, { method: "POST" }),
        cancelarComoBanco: (id, motivo) => this.request(`/v1/portal/banco/portabilidade/${id}/cancelar`, {
            method: "POST",
            body: motivo ? { motivo } : {},
        }),
        // === Averbadora (visao global) ===
        todas: (q) => this.request("/v1/admin/portabilidade", { query: q ?? {} }),
        detalheAdmin: (id) => this.request(`/v1/admin/portabilidade/${id}`),
        comprovanteAdminUrl: (id) => `${this.opts.baseUrl}/v1/admin/portabilidade/${id}/comprovante`,
        analisarComprovante: (id, versao, decisao, motivo) => this.request(`/v1/admin/portabilidade/${id}/analise`, { method: "POST", body: { versao, decisao, motivo } }),
    };
    // ============ Refinanciamento no proprio banco ============
    // O servidor pede  // ============ Refinanciamento no proprio banco ============
    // O servidor pede (ou o banco oferta); o aceite gera o contrato REFIN sucessor.
    refinanciamento = {
        // === Servidor ===
        elegiveis: (matricula) => this.request("/v1/servidores/me/refinanciamentos/elegiveis", { query: matricula ? { matricula } : {} }),
        meus: (matricula) => this.request("/v1/servidores/me/refinanciamentos", {
            query: matricula ? { matricula } : {},
        }),
        meu: (id) => this.request(`/v1/servidores/me/refinanciamentos/${id}`),
        solicitar: (body) => this.request("/v1/servidores/me/refinanciamentos", { method: "POST", body }),
        aceitar: (id, senha) => this.request(`/v1/servidores/me/refinanciamentos/${id}/aceitar`, {
            method: "POST",
            body: { senha },
        }),
        recusar: (id, motivo) => this.request(`/v1/servidores/me/refinanciamentos/${id}/recusar`, {
            method: "POST",
            body: { motivo },
        }),
        cancelarPedido: (id, motivo) => this.request(`/v1/servidores/me/refinanciamentos/${id}/cancelar`, {
            method: "POST",
            body: { motivo },
        }),
        // === Banco ===
        doBanco: (estado) => this.request("/v1/portal/banco/refinanciamentos", {
            query: estado ? { estado } : {},
        }),
        doBancoDetalhe: (id) => this.request(`/v1/portal/banco/refinanciamentos/${id}`),
        elegiveisNoVinculo: (adf) => this.request("/v1/portal/banco/refinanciamentos/elegiveis", {
            query: { adf },
        }),
        ofertar: (body) => this.request("/v1/portal/banco/refinanciamentos", { method: "POST", body }),
        responder: (id, body) => this.request(`/v1/portal/banco/refinanciamentos/${id}/responder`, { method: "POST", body }),
        cancelarPeloBanco: (id, motivo) => this.request(`/v1/portal/banco/refinanciamentos/${id}/cancelar`, {
            method: "POST",
            body: { motivo },
        }),
        // === Averbadora ===
        todos: (q) => this.request("/v1/admin/refinanciamentos", { query: q ?? {} }),
        regras: (convenioId) => this.request(`/v1/admin/convenios/${encodeURIComponent(convenioId)}/refinanciamento`),
        salvarRegras: (convenioId, regras) => this.request(`/v1/admin/convenios/${encodeURIComponent(convenioId)}/refinanciamento`, { method: "PUT", body: regras }),
    };
    // ============ Servidor (marketplace + demais consultas do proprio servidor) ============
    servidor = {
        /** Marketplace de ofertas — derivado das tabelas de emprestimo publicadas pelos bancos.
         *  Passe `matricula` pra filtrar so ofertas dos convenios da prefeitura dessa matricula
         *  (evita servidor de Palhoca ver oferta que so vale em Florianopolis).
         *
         *  Uma entrada por TABELA vigente, nao por banco: o mesmo banco aparece
         *  varias vezes quando publica faixas diferentes (3% ate 30x, 5% ate 90x).
         *  Use `montarOpcoesDeCredito` (@atlas/domain) pra agrupar por banco e
         *  cruzar com a margem — e' o que a vitrine do servidor faz. */
        ofertas: (matricula) => this.request(matricula
            ? `/v1/servidores/me/ofertas?matricula=${encodeURIComponent(matricula)}`
            : "/v1/servidores/me/ofertas"),
        /** Ofertas ativas criadas pelos bancos que casam com o perfil do servidor.
         *  Passe `matricula` pra respeitar a matricula ativa no switcher (senao usa
         *  a do login, o que confunde quem tem acumulacao de cargos). */
        getMyOfertasBanco: (matricula) => this.request(matricula
            ? `/v1/servidores/me/ofertas-banco?matricula=${encodeURIComponent(matricula)}`
            : "/v1/servidores/me/ofertas-banco"),
        /** Beneficios/descontos da prefeitura do servidor. Passe matricula pra respeitar
         *  o switcher (senao usa a prefeitura do JWT, que fixa a matricula do login). */
        getMyBeneficios: (categoria, matricula) => this.request("/v1/servidores/me/beneficios", {
            query: {
                ...(categoria ? { categoria } : {}),
                ...(matricula ? { matricula } : {}),
            },
        }),
        /** Servidor solicita uma proposta (pré-reserva) — CRIA no store do banco (o banco recebe). */
        /** Status da SENHA DE TRANSACAO — segundo fator separado da senha de login
         *  usado quando a prefeitura marca exigeSenhaProposta ou exigeSenhaAverbacao.
         *  Frontend usa pra decidir se abre o modal obrigatorio de cadastro. */
        transactionPasswordStatus: () => this.request("/v1/servidores/me/transaction-password/status"),
        /** Define ou troca a senha de transacao. Politica: 8+ chars, com maiuscula,
         *  minuscula, digito e caractere especial. Troca exige senhaAtual. Primeira
         *  vez (nao ha hash ainda) so precisa da novaSenha. */
        /** `codigo` (6 digitos, de POST /me/codigo) e' OBRIGATORIO no cadastro da
         *  1a senha de transacao. Na redefinicao vale senhaAtual ou senhaLogin. */
        setTransactionPassword: (body) => this.request("/v1/servidores/me/transaction-password", {
            method: "POST",
            body,
        }),
        /** "Ver senha": confirma a SENHA DE LOGIN e devolve a senha de transacao em
         *  claro (cofre cifrado no servidor). Usado no card de /servidor/conta. */
        revealTransactionPassword: (body) => this.request("/v1/servidores/me/transaction-password/reveal", {
            method: "POST",
            body,
        }),
        criarProposta: (input, opts) => this.request("/v1/servidores/me/propostas", { method: "POST", body: input, signal: opts?.signal }),
        /** Registra o clique do servidor no botao "Acessar" de um beneficio.
         *  Best-effort — o botao abre a URL de qualquer jeito (nao bloqueia se falhar). */
        registrarCliqueBeneficio: (beneficioId, input) => this.request(`/v1/servidores/me/beneficios/${encodeURIComponent(beneficioId)}/clique`, { method: "POST", body: input ?? {} }),
        /** Solicita cotacao de telemedicina — a averbadora recebe os dados do servidor
         *  (principalmente o telefone) pra entrar em contato e formalizar o plano. */
        solicitarCotacaoTelemedicina: (input) => this.request("/v1/servidores/me/telemedicina/cotacao", { method: "POST", body: input ?? {} }),
        /** Cotacoes de telemedicina DO PROPRIO servidor — pra esconder o botao (mostra
         *  "em analise") e listar na aba Em Analise. */
        minhasCotacoesTelemedicina: () => this.request("/v1/servidores/me/telemedicina/cotacoes"),
        /** Vitrine (carrossel) mostrada no dashboard do servidor — banners ativos
         *  cadastrados pela averbadora em /averbadora/vitrine.
         *
         *  Passe a matricula pra respeitar o switcher: a vitrine mostra so bancos
         *  que atendem a prefeitura daquele vinculo. Sem o parametro, o backend
         *  usa a matricula do JWT. */
        vitrine: (matricula) => this.request("/v1/servidores/me/vitrine", { query: matricula ? { matricula } : undefined }),
        /** Registra impressao de um banner (fire-and-forget do card visto). */
        vitrineImpressao: (bannerId) => this.request(`/v1/servidores/me/vitrine/${bannerId}/impressao`, { method: "POST" }),
        /** Registra clique num banner — alimenta a receita CPC da averbadora. */
        vitrineClique: (bannerId) => this.request(`/v1/servidores/me/vitrine/${bannerId}/clique`, { method: "POST" }),
        /** Solicita cartao consignado ou cartao beneficio. Nao cria contrato tradicional
         *  (o modelo ainda so aceita EMPRESTIMO/REFIN/ECONSIGNADO) — registra a
         *  solicitacao pra averbadora e devolve um protocolo. O banco recebe pra
         *  emitir/ativar o cartao via canal proprio (padrao do mercado). */
        /**
         * Os bancos que ofertam o produto ao servidor — os convenios ativos da
         * organizacao da matricula que declararam verba para ele. E' a lista que
         * a tela de cartao mostra para escolher; vazia = ninguem oferta ainda.
         */
        bancosDoProduto: (produto, matricula) => this.request("/v1/servidores/me/bancos-do-produto", {
            query: { produto, ...(matricula ? { matricula } : {}) },
        }),
        solicitarCartao: (input, opts) => this.request("/v1/servidores/me/cartoes", { method: "POST", body: input, signal: opts?.signal }),
        /** Propostas/pré-reservas do próprio servidor (mesma fonte que o banco lê).
         *  Filtra pela matrícula ativa quando informada — evita misturar histórico
         *  entre matrículas de servidor com acumulação de cargos. */
        /** Comunicados publicados pela averbadora com publico=servidor (usados no carrossel do dashboard). */
        comunicados: () => this.request("/v1/servidores/me/comunicados"),
        historicoPagamentosContrato: (adf) => this.request(`/v1/servidores/me/contratos/${encodeURIComponent(adf)}/historico-pagamentos`),
        /** Baixa o CCB (contrato assinado que o banco anexou) como Blob. Envia
         *  Authorization Bearer. Lanca erro com status HTTP e reason (extraido do
         *  body JSON quando o backend explica a causa) — o front distingue os
         *  varios 404 (banco nao anexou vs contrato nao achado vs arquivo sumiu). */
        baixarContratoCcb: async (adf) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/servidores/me/contratos/${adf}/ccb.pdf`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) {
                let reason;
                try {
                    const body = (await res.json());
                    reason = body?.reason;
                }
                catch {
                    /* body nao era JSON — sem reason */
                }
                const err = new Error(`Falha ao baixar contrato (${res.status}${reason ? ` — ${reason}` : ""})`);
                err.status = res.status;
                if (reason)
                    err.reason = reason;
                throw err;
            }
            return await res.blob();
        },
        propostas: (matricula) => this.request("/v1/servidores/me/propostas", { query: matricula ? { matricula } : undefined }),
        // Averbacoes iniciadas pelo banco pendentes de confirmacao do servidor
        // (fluxo "empréstimo iniciado pelo banco" quando prefeitura exige senha).
        averbacoesPendentes: () => this.request("/v1/servidores/me/averbacoes-pendentes"),
        confirmarAverbacaoPendente: (adf, senha) => this.request(`/v1/servidores/me/averbacoes-pendentes/${adf}/confirmar`, { method: "POST", body: { senha } }),
        rejeitarAverbacaoPendente: (adf, senha, motivo) => this.request(`/v1/servidores/me/averbacoes-pendentes/${adf}/rejeitar`, { method: "POST", body: { senha, motivo } }),
        /**
         * Pede o cancelamento de uma consignacao facultativa (#276). O pedido NAO
         * encerra o desconto: quem encerra e' a consignataria. 409
         * `solicitacao_ja_aberta` se ja' houver pedido aberto.
         */
        /** #282: envia ao e-mail do servidor o codigo de 6 digitos para autorizar. */
        pedirCodigoAutorizacao: (consignacaoId) => this.request(`/v1/servidores/me/consignacoes/${encodeURIComponent(consignacaoId)}/autorizacao/codigo`, { method: "POST", body: {} }),
        /**
         * #282: o termo que o servidor vai aceitar, montado no servidor. A tela mostra
         * `texto` e devolve `hashDoTermo` no autorizar: se a pendencia mudou no meio,
         * a autorizacao e' recusada com 409 `termo_desatualizado`.
         */
        termoDeAutorizacao: (consignacaoId) => this.request(`/v1/servidores/me/consignacoes/${encodeURIComponent(consignacaoId)}/autorizacao/termo`),
        /** #282: autoriza pela tela (`web`, sempre com a senha) ou pelo codigo (`token`), com o hash do termo que a tela mostrou. */
        autorizarConsignacao: (consignacaoId, body) => this.request(`/v1/servidores/me/consignacoes/${encodeURIComponent(consignacaoId)}/autorizar`, { method: "POST", body }),
        solicitarCancelamentoConsignacao: (consignacaoId, motivo) => this.request(`/v1/servidores/me/consignacoes/${encodeURIComponent(consignacaoId)}/solicitar-cancelamento`, { method: "POST", body: motivo ? { motivo } : {} }),
        /** O servidor pede a alteracao de um beneficio: o assunto e o que quer mudar (10+ caracteres). */
        solicitarAlteracaoConsignacao: (consignacaoId, body) => this.request(`/v1/servidores/me/consignacoes/${encodeURIComponent(consignacaoId)}/solicitar-alteracao`, { method: "POST", body }),
        /** Os pedidos do servidor (abertos e respondidos), o mais novo primeiro, com a resposta. */
        minhasSolicitacoesConsignacao: () => this.request("/v1/servidores/me/consignacoes/solicitacoes"),
        /** Redefinicao direta (sem codigo por e-mail) — DEPRECIADA em favor de
         *  pedirCodigoSenha + confirmarNovaSenha. Mantida no SDK pra outros
         *  clientes (ex.: mobile) que ainda usem. */
        redefinirSenha: (input) => this.request("/v1/servidores/me/senha/redefinir", {
            method: "POST",
            body: { senha_atual: input.senhaAtual, nova_senha: input.novaSenha },
        }),
        /** Passo 1 do fluxo com verificacao por e-mail: pede o backend gerar um
         *  codigo de 6 digitos e enviar pro e-mail cadastrado.
         *
         *  `codigo_teste` so vem em DESENVOLVIMENTO (APP_ENV dev/local). Em
         *  producao e homologacao, envio que falha responde `enviado: false` +
         *  `motivo` e NENHUM codigo — o fluxo fica bloqueado de proposito, porque
         *  devolver o codigo no mesmo canal anula a verificacao que ele faz.
         *  Quem chama tem que tratar `enviado: false` sem codigo como bloqueio, e
         *  nao avancar de passo. */
        pedirCodigoSenha: () => this.request("/v1/servidores/me/codigo", { method: "POST" }),
        /** Passo 2: valida senha atual + codigo do e-mail e persiste a nova senha.
         *  Backend recusa se codigo estiver invalido/expirado (401) ou senha atual
         *  errada (401). Depois de sucesso o codigo e removido do KV. */
        confirmarNovaSenha: (input) => this.request("/v1/servidores/me/senha", {
            method: "POST",
            body: { senha_atual: input.senhaAtual, nova_senha: input.novaSenha, codigo: input.codigo },
        }),
        /** Cancela um codigo pendente (deleta do KV). Chamado quando o usuario
         *  clica "Cancelar" apos ja ter pedido o codigo — evita reuso posterior.
         *  Vale tanto pro fluxo de senha quanto pro fluxo de contato — mesma chave
         *  no KV (`chg:${cpf}`). */
        cancelarCodigoSenha: () => this.request("/v1/servidores/me/codigo", { method: "DELETE" }),
        /** Passo 2 do fluxo de contato: valida o codigo + persiste e-mail/telefone.
         *  Fluxo:
         *  1) pedirCodigoSenha() envia o codigo (endpoint compartilhado com senha).
         *  2) Usuario digita.
         *  3) atualizarContato({ codigo, email, telefone }) grava.
         *  Backend recusa se codigo estiver invalido/expirado (401). Sucesso remove
         *  o codigo do KV automaticamente. */
        atualizarContato: (input) => this.request("/v1/servidores/me/contato", {
            method: "POST",
            body: { codigo: input.codigo, email: input.email, telefone: input.telefone },
        }),
        /** Le o termo renderizado (placeholders substituidos com vars). Fonte: /averbadora/termos. */
        getTermo: (tipo, vars) => {
            const query = {};
            if (vars && Object.keys(vars).length > 0)
                query.vars = JSON.stringify(vars);
            return this.request(`/v1/servidores/me/termos/${tipo}`, { query });
        },
        /** Lista todos os termos ativos em vigencia (metadados, sem corpo). Usado
         *  na tela Conta > Suporte pra o servidor conferir o que esta vigente. */
        listTermos: () => this.request("/v1/servidores/me/termos"),
        /** Info de suporte (email/whatsapp/horario/frase de abertura) que a averbadora
         *  edita em /averbadora/suporte. Servidor usa no modal de Conta > Suporte. */
        getSuporte: () => this.request("/v1/servidores/me/suporte"),
    };
    // ============ Portal Banco ============
    banco = {
        me: () => this.request("/v1/portal/banco/me"),
        /** Manual de uso do portal (PDF) como Blob. A rota exige Authorization —
         *  por isso não há URL para `<a href>`: o manual não é público. */
        manualBlob: () => this.baixarManual("/v1/portal/banco/manual"),
        /** Credenciais de API DESTE banco. A chave completa vem so em sandbox. */
        apiTokens: () => this.request("/v1/portal/banco/api-tokens"),
        /** URLs base da API por ambiente (homologacao/producao), configuradas pela averbadora. */
        apiAmbientes: () => this.request("/v1/portal/banco/api-ambientes"),
        /**
         * Regras de cada convenio, para a documentacao de integracao.
         *
         * Diferente de `convenios()` pela FORMA, nao pela honestidade: os dois usam
         * `null` para "nao configurado" desde 14/08/2026, quando os padroes inventados
         * (`?? 96` parcelas, `?? 1.8%`) sairam de todos os endpoints. Aqui as regras
         * vem separadas entre o que a API valida e o que e so informativo, que e o
         * recorte que a documentacao precisa e o formulario nao.
         */
        convenioRegras: () => this.request("/v1/portal/banco/convenios/regras"),
        convenios: () => this.request("/v1/portal/banco/convenios"),
        setConvenioAtivo: (convenioId) => this.request("/v1/portal/banco/convenio-ativo", {
            method: "POST",
            body: { convenioId },
        }),
        visaoGeral: () => this.request("/v1/portal/banco/visao-geral"),
        comunicados: () => this.request("/v1/portal/banco/comunicados"),
        margemBuscar: (input) => this.request("/v1/portal/banco/margem/buscar", { method: "POST", body: input }),
        margemExemplos: () => this.request("/v1/portal/banco/margem/exemplos"),
        /** Proximo numero de contrato do banco (sequencial). Preview — quem consome
         *  a sequencia e' a gravacao do contrato, entao o numero final pode diferir
         *  se outra averbacao entrar no meio. */
        proximoNumeroContrato: () => this.request("/v1/portal/banco/contratos/proximo-numero"),
        margemCalcular: (idMatricula, input) => this.request(`/v1/portal/banco/margem/${idMatricula}/calcular`, { method: "POST", body: input }),
        // Averbacao INICIADA PELO BANCO (emprestimo, cartao consignado ou beneficio).
        // Se a prefeitura marcou exigeSenhaAverbacao=true, contrato vira "Aguardando
        // Confirmacao do Servidor" — o servidor precisa confirmar com senha em
        // /me/averbacoes-pendentes/:adf/confirmar. Se false, ja vira Ativo direto.
        averbarDireto: (idMatricula, input) => this.request(`/v1/portal/banco/margem/${idMatricula}/averbar`, { method: "POST", body: input }),
        // RESERVA DE CARTAO (ADR-0014): compromete a margem do balde de cartao e
        // nao desconta por si. `valor` ausente = o disponivel inteiro do balde.
        // A senha do servidor vale como na averbacao.
        reservarCartao: (idMatricula, input) => this.request(`/v1/portal/banco/margem/${idMatricula}/reservas-cartao`, { method: "POST", body: input }),
        /** A reserva de cartao com os lancamentos e o que a folha fez com cada um. */
        lancamentosDaReserva: (adf) => this.request(`/v1/portal/banco/reservas-cartao/${adf}/lancamentos`),
        /** Informa (ou substitui) o lancamento de uma competencia aberta. */
        informarLancamentoCartao: (adf, input) => this.request(`/v1/portal/banco/reservas-cartao/${adf}/lancamentos`, { method: "POST", body: input }),
        /** Cancela o lancamento de uma competencia antes do corte. */
        cancelarLancamentoCartao: (adf, competencia) => this.request(`/v1/portal/banco/reservas-cartao/${adf}/lancamentos/${competencia}`, { method: "DELETE" }),
        // Editar proposta: fluxo de negociacao offline. Banco liga pro servidor
        // pra negociar valor/parcelas, servidor concorda, banco edita aqui e
        // depois aprova. Servidor recebe notif do que foi acordado.
        editarProposta: (adf, input) => this.request(`/v1/portal/banco/contratos/${adf}/editar`, { method: "POST", body: input }),
        contratos: (filtros = {}) => this.request("/v1/portal/banco/contratos", {
            query: {
                colaborador: filtros.colaborador,
                ...(filtros.incluirTodosConvenios ? { incluir_todos_convenios: "true" } : {}),
                ...(filtros.situacao
                    ? Object.fromEntries(filtros.situacao.map((s, i) => [`situacao_${i}`, s]))
                    : {}),
            },
        }),
        /**
         * O credor informa o valor solicitado e a taxa a.m. de um contrato LEGADO
         * (feature 012). Só o banco dono do contrato; contrato da Atlas responde 400.
         * A parcela não muda — é da folha. `taxaAm` em fração (0.0189).
         */
        informarCronogramaLegado: (adf, body) => this.request(`/v1/portal/banco/contratos/${encodeURIComponent(adf)}/cronograma`, { method: "POST", body }),
        informarDadosLegado: (adf, body) => this.request(`/v1/portal/banco/contratos/${adf}/dados-financeiros`, {
            method: "POST",
            body,
        }),
        previaDesaverbacao: (adf) => this.request(`/v1/portal/banco/contratos/${encodeURIComponent(adf)}/previa-desaverbacao`),
        getContrato: (adf) => this.request(`/v1/portal/banco/contratos/${adf}`),
        averbar: (tipo, body) => this.request(`/v1/portal/banco/contratos/averbar/${tipo}`, { method: "POST", body }),
        reservar: (tipo, body) => this.request(`/v1/portal/banco/contratos/reservar/${tipo}`, {
            method: "POST",
            body,
        }),
        acao: (adf, acao, body) => this.request(`/v1/portal/banco/contratos/${adf}/${acao}`, {
            method: "POST",
            body: body ?? {},
        }),
        // Contratos com falha em folha reportada pela averbadora — banco precisa tratar.
        listContratosFalha: () => this.request("/v1/portal/banco/falhas"),
        tratarFalha: (adf, acao, motivo) => this.request(`/v1/portal/banco/tratar-falha/${adf}`, {
            method: "POST",
            body: { acao, motivo },
        }),
        comprovanteUrl: (adf) => new URL(`/v1/portal/banco/contratos/${adf}/comprovante.pdf`, this.opts.baseUrl).toString(),
        // Cadastros
        listTabelas: (q) => this.request("/v1/portal/banco/cadastros/tabela-emprestimos", { query: q ?? {} }),
        getTabela: (id) => this.request(`/v1/portal/banco/cadastros/tabela-emprestimos/${id}`),
        upsertTabela: (body) => this.request("/v1/portal/banco/cadastros/tabela-emprestimos", {
            method: "POST",
            body,
        }),
        removerTabela: (id) => this.request(`/v1/portal/banco/cadastros/tabela-emprestimos/${id}`, { method: "DELETE" }),
        /** Faz upload da CCB assinada (PDF) pro R2. Retorna a chave persistida. */
        uploadCcb: async (adf, file) => {
            const fd = new FormData();
            fd.append("adf", adf);
            fd.append("file", file);
            return this.request("/v1/portal/banco/ccb/upload", {
                method: "POST",
                body: fd,
                isFormData: true,
            });
        },
        /** URL absoluta para visualizar a CCB (requer sessao do proprio banco). */
        ccbUrl: (key) => new URL(`/v1/portal/banco/ccb/${key}`, this.opts.baseUrl).toString(),
        /** Baixa o CCB anexado como Blob (envia Authorization). Necessario
         *  porque o endpoint exige Bearer — abrir a ccbUrl direto no <a href>
         *  volta 401. Use com URL.createObjectURL pra abrir/salvar. */
        fetchCcbBlob: async (key) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/portal/banco/ccb/${key}`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao baixar CCB (${res.status})`);
            return await res.blob();
        },
        reativarTabela: (id) => this.request(`/v1/portal/banco/cadastros/tabela-emprestimos/${id}/reativar`, {
            method: "POST",
        }),
        // Sub organizacoes do banco — opcoes do campo do operador.
        listSubOrganizacoes: (q) => this.request("/v1/portal/banco/cadastros/sub-organizacoes", { query: q ?? {} }),
        getSubOrganizacao: (id) => this.request(`/v1/portal/banco/cadastros/sub-organizacoes/${id}`),
        upsertSubOrganizacao: (body) => this.request("/v1/portal/banco/cadastros/sub-organizacoes", {
            method: "POST",
            body,
        }),
        removerSubOrganizacao: (id) => this.request(`/v1/portal/banco/cadastros/sub-organizacoes/${id}`, { method: "DELETE" }),
        reativarSubOrganizacao: (id) => this.request(`/v1/portal/banco/cadastros/sub-organizacoes/${id}/reativar`, {
            method: "POST",
        }),
        /**
         * Operadores do banco. Sem `limit`, devolve a lista inteira (C1).
         *
         * `somenteAdmin` SAIU em 04/09/2026: compilava para `perfil = 'admin'`, o
         * mesmo que `perfil: "admin"` — dois caminhos para o mesmo recorte. E
         * combinado com outro `perfil` os dois predicados eram AND-ados e a
         * resposta vinha vazia sem erro nenhum.
         */
        listUsuarios: (q) => this.request("/v1/portal/banco/cadastros/usuarios", { query: q ?? {} }),
        getUsuario: (id) => this.request(`/v1/portal/banco/cadastros/usuarios/${id}`),
        upsertUsuario: (body) => this.request("/v1/portal/banco/cadastros/usuarios", {
            method: "POST",
            body,
        }),
        removerUsuario: (id) => this.request(`/v1/portal/banco/cadastros/usuarios/${id}`, { method: "DELETE" }),
        reativarUsuario: (id) => this.request(`/v1/portal/banco/cadastros/usuarios/${id}/reativar`, { method: "POST" }),
        /** O ADMIN do banco redefine a senha de um operador. A senha nasce
         *  temporaria — o operador e' obrigado a troca-la no proximo login. */
        redefinirSenhaUsuario: (id, password) => this.request(`/v1/portal/banco/cadastros/usuarios/${id}/redefinir-senha`, {
            method: "POST",
            body: { password },
        }),
        /** O OPERADOR troca a propria senha. E' o unico caminho que limpa a marca de
         *  senha temporaria, porque e' o unico em que so' o dono a conhece. */
        trocarMinhaSenha: (senhaAtual, novaSenha) => this.request("/v1/portal/banco/conta/senha", {
            method: "POST",
            body: { senha_atual: senhaAtual, nova_senha: novaSenha },
        }),
        /** Devolve o CPF completo do usuario (acesso registrado em audit log no servidor). */
        revealUsuarioCpf: (id) => this.request(`/v1/portal/banco/cadastros/usuarios/${id}/cpf`),
        perfilPresetsBanco: () => this.request("/v1/portal/banco/perfil-presets"),
        criarPerfilPresetBanco: (body) => this.request("/v1/portal/banco/perfil-presets", {
            method: "POST",
            body,
        }),
        // Relatorios
        /** `subOrganizacao` = id da sub organização, `principal` ou `nao-registrado`. */
        relatorioConsignacoes: (q) => this.request("/v1/portal/banco/relatorios/consignacoes", { query: q ?? {} }),
        relatorioFaturamento: () => this.request("/v1/portal/banco/relatorios/faturamento"),
        // Bate de carteira mensal (escopado ao banco logado — jwt.banco_id).
        // Substitui o baterCarteira() client-side que era pseudo-random.
        bateCarteira: (body) => this.request("/v1/portal/banco/bate-carteira", { method: "POST", body }),
        /** O mesmo relatorio em .xlsx, inteiro (GET, para o download autenticado). */
        bateCarteiraPlanilhaUrl: (competencia) => `${this.opts.baseUrl}/v1/portal/banco/bate-carteira/planilha?competencia=${encodeURIComponent(competencia)}`,
        // ===== Troca de arquivos do banco: os retornos gerados PARA ele =====
        retornos: {
            /** Paginado no servidor; so' o que foi gerado para o banco logado. */
            listar: (f = {}) => {
                const qs = new URLSearchParams();
                for (const [k, v] of Object.entries(f)) {
                    if (v !== undefined && v !== "")
                        qs.set(k, String(v));
                }
                return this.request(`/v1/portal/banco/retornos?${qs}`);
            },
            /** Rota autenticada: a tela busca com token e monta o blob. */
            arquivoUrl: (id, formato = "txt") => `${this.opts.baseUrl}/v1/portal/banco/retornos/${encodeURIComponent(id)}/arquivo?formato=${formato}`,
        },
        // ===== Modo operadora: a consignataria de servicos (#312) =====
        // So' para credor com `modo_portal` operadora ou ambos; credito responde 403
        // `modo_portal_nao_habilitado`.
        operadora: {
            visaoDoMes: () => this.request("/v1/portal/banco/operadora/visao-do-mes"),
        },
        // Os pedidos de cancelamento dos servidores, roteados a' consignataria dona
        // da rubrica (#318). Ver: `beneficios` ou `solicitacoes`; responder: `solicitacoes`.
        pedidosCancelamento: {
            listar: (f = {}) => this.request("/v1/portal/banco/solicitacoes", {
                query: Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined)),
            }),
            contagem: () => this.request("/v1/portal/banco/solicitacoes/contagem"),
            /** A previa do encerramento do beneficio do pedido — a mesma da #317. */
            previa: (id, competenciaFim) => this.request(`/v1/portal/banco/solicitacoes/${id}/previa`, {
                query: competenciaFim ? { competenciaFim } : {},
            }),
            /** A previa da alteracao de valor que atende um pedido de alteracao — a mesma da #320. */
            previaAlteracao: (id, q = {}) => this.request(`/v1/portal/banco/solicitacoes/${id}/previa-alteracao`, {
                query: Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined)),
            }),
            responder: (id, body) => this.request(`/v1/portal/banco/solicitacoes/${id}/responder`, { method: "POST", body }),
        },
        // Os beneficios (consignacoes facultativas) da consignataria — #315. Mesmo
        // portao de modo da `operadora`; beneficio de outra responde 404.
        beneficios: {
            listar: (f = {}) => this.request("/v1/portal/banco/beneficios", {
                query: Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v !== "")),
            }),
            /** Os ids tem `:` — vao codificados. */
            detalhe: (id) => this.request(`/v1/portal/banco/beneficios/${encodeURIComponent(id)}`),
            /** O que encerrar faz na margem e a partir de quando (#317). Sem competencia, a padrao. */
            previaEncerramento: (id, competenciaFim) => this.request(`/v1/portal/banco/beneficios/${encodeURIComponent(id)}/encerrar/previa`, {
                query: competenciaFim ? { competenciaFim } : {},
            }),
            /** `competenciaFim` = o ultimo desconto; a seguinte ja' sai sem a linha. */
            encerrar: (id, body) => this.request(`/v1/portal/banco/beneficios/${encodeURIComponent(id)}/encerrar`, { method: "POST", body }),
            /** Os orgaos e as rubricas facultativas em que a consignataria pode incluir (#320). */
            opcoesInclusao: () => this.request("/v1/portal/banco/beneficios/opcoes-inclusao"),
            /** Quem e' a matricula e o que a inclusao faz na margem (#320). Sem competencia, a padrao. */
            previaInclusao: (q) => this.request("/v1/portal/banco/beneficios/previa-inclusao", {
                query: Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)])),
            }),
            incluir: (body) => this.request("/v1/portal/banco/beneficios", { method: "POST", body }),
            /** A diferenca na margem e se o aumento espera o servidor (#320). */
            previaValor: (id, q = {}) => this.request(`/v1/portal/banco/beneficios/${encodeURIComponent(id)}/valor/previa`, {
                query: Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)])),
            }),
            alterarValor: (id, body) => this.request(`/v1/portal/banco/beneficios/${encodeURIComponent(id)}/valor`, { method: "PATCH", body }),
        },
        // ===== Envio do arquivo mensal da consignataria, pelo proprio banco =====
        // So' para os orgaos vinculados (convenio ativo ou rubrica). Envia e ve a
        // previa; a confirmacao e' da averbadora, por isso nao ha' `confirmar` aqui.
        arquivos: {
            orgaos: () => this.request("/v1/portal/banco/arquivos/orgaos"),
            listar: (f = {}) => {
                const qs = new URLSearchParams();
                for (const [k, v] of Object.entries(f)) {
                    if (v !== undefined && v !== "")
                        qs.set(k, String(v));
                }
                return this.request(`/v1/portal/banco/arquivos?${qs}`);
            },
            enviar: (unidadeId, arquivo, nome) => {
                const fd = new FormData();
                fd.append("unidadeId", String(unidadeId));
                fd.append("arquivo", arquivo, nome ?? arquivo.name ?? "arquivo.txt");
                return this.request("/v1/portal/banco/arquivos", { method: "POST", body: fd, isFormData: true });
            },
            previa: (arquivoId) => this.request(`/v1/portal/banco/arquivos/${encodeURIComponent(arquivoId)}/previa`),
        },
        // ===== Ofertas de credito (banco -> servidores) =====
        ofertas: {
            // Pagina no Postgres (feature 006). `limit` ausente devolve a lista
            // inteira do escopo, como sempre (regra C1).
            list: (q) => this.request("/v1/portal/banco/ofertas", { query: q ?? {} }),
            upsert: (body) => this.request("/v1/portal/banco/ofertas", { method: "POST", body }),
            pausar: (id) => this.request(`/v1/portal/banco/ofertas/${id}/pausar`, { method: "PATCH" }),
            reativar: (id) => this.request(`/v1/portal/banco/ofertas/${id}/reativar`, { method: "PATCH" }),
        },
    };
    // ============ Admin (Averbadora) ============
    admin = {
        /**
         * Troca de arquivos de TODAS as folhas.
         *
         * Mesmo histórico da prefeitura, sem o escopo de uma só — é por aqui que a
         * averbadora vê se o retorno do mês chegou, de quais folhas, e por quem.
         */
        /**
         * Arquivos trocados com as prefeituras. Sempre paginada — nasceu assim,
         * antes do contrato `limit`/`offset`, e por isso C1 nao vale aqui: sem
         * parametro ela devolve 10 linhas, nao a lista inteira.
         *
         * `limite` (o nome antigo) segue aceito pela rota como ALIAS de `limit`,
         * mas nao e mais oferecido aqui: um dialeto a menos na superficie que a
         * web enxerga.
         */
        trocaArquivos: (f = {}) => {
            const qs = new URLSearchParams();
            for (const [k, v] of Object.entries(f)) {
                if (v !== undefined && v !== "")
                    qs.set(k, String(v));
            }
            return this.request(`/v1/admin/troca-arquivos?${qs}`);
        },
        trocaArquivoUrl: (id) => `${this.opts.baseUrl}/v1/admin/troca-arquivos/${encodeURIComponent(id)}/arquivo`,
        /** O relatório de conversão (retorno) ou de geração (movimento), em JSON. */
        trocaArquivoRelatorioUrl: (id) => `${this.opts.baseUrl}/v1/admin/troca-arquivos/${encodeURIComponent(id)}/relatorio`,
        /** O relatório de TOMBAMENTO de uma carga inicial (ADR-0010), em JSON:
         *  contratos criados, não tombados com motivo, quarentena e recusas. */
        trocaArquivoTombamento: async (id) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/admin/troca-arquivos/${encodeURIComponent(id)}/tombamento`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao abrir o relatório de tombamento (${res.status})`);
            return await res.blob();
        },
        /**
         * Relê a posição de cada dívida legada na linha do pacote de onde ela saiu.
         *
         * ENSAIO por padrão: sem `aplicar`, devolve o plano e não grava nada. Com
         * `aplicar: true`, grava contratos e quarentena numa transação, audita e
         * guarda o plano completo ao lado do pacote. Idempotente.
         */
        alinharCronogramasPelaOrigem: (id, opts = {}) => this.request(`/v1/admin/troca-arquivos/${encodeURIComponent(id)}/alinhar-cronogramas`, { method: "POST", body: { aplicar: opts.aplicar === true } }),
        /**
         * Recupera a dívida que a quarentena juntou com uma linha idêntica anterior,
         * relendo o pacote guardado.
         *
         * ENSAIO por padrão: sem `aplicar`, devolve o plano (com a previsão de
         * promoção) e não grava nada. Com `aplicar: true`, grava registros e
         * contratos numa transação, audita e guarda o plano ao lado do pacote.
         * Idempotente.
         */
        separarLinhasIdenticas: (id, opts = {}) => this.request(`/v1/admin/troca-arquivos/${encodeURIComponent(id)}/separar-linhas-identicas`, { method: "POST", body: { aplicar: opts.aplicar === true } }),
        /** O relatório completo, para a tela renderizar. Ver o gêmeo em `prefeitura`. */
        trocaArquivoRelatorio: async (id) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/admin/troca-arquivos/${encodeURIComponent(id)}/relatorio`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao abrir o relatório (${res.status})`);
            return (await res.json());
        },
        /**
         * LAYOUTS — o catálogo e a atribuição por unidade pagadora.
         *
         * O catálogo é constante de código: quem inclui layout novo é a engenharia.
         * O que estas chamadas fazem é escolher qual deles vale em cada unidade.
         */
        layouts: {
            catalogo: () => this.request("/v1/admin/layouts"),
            /** Feature 008 — o hub: todas as unidades, cobertura, catálogo com uso e última prova. */
            hub: (f = {}) => {
                const q = new URLSearchParams();
                for (const [k, v] of Object.entries(f))
                    if (v !== undefined && v !== null && String(v) !== "")
                        q.set(k, String(v));
                const s = q.toString();
                return this.request(`/v1/admin/layouts/hub${s ? `?${s}` : ""}`);
            },
            matriz: () => this.request("/v1/admin/layouts/matriz"),
            ficha: (codigo, competencia) => this.request(`/v1/admin/layouts/${encodeURIComponent(codigo)}${competencia ? `?competencia=${encodeURIComponent(competencia)}` : ""}`),
            exemploUrl: (codigo) => `${this.opts.baseUrl}/v1/admin/layouts/${encodeURIComponent(codigo)}/exemplo.zip`,
            /** O zip de exemplo como blob — a rota exige Authorization, como o documento. */
            exemploBlob: async (codigo) => {
                const token = await this.storage.getAccess();
                const url = new URL(`/v1/admin/layouts/${encodeURIComponent(codigo)}/exemplo.zip`, this.opts.baseUrl).toString();
                const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                if (!res.ok)
                    throw new Error(`Falha ao baixar o exemplo do layout (${res.status})`);
                return await res.blob();
            },
            /**
             * O ENSAIO DE ENTRADA — "este arquivo passa?", sem folha e sem gravar nada.
             *
             * Os três vereditos voltam como sucesso (`200`): `recusado` e `ambiguo`
             * são a resposta que o operador veio buscar. Só pedido malformado
             * (competência torta, layout inexistente, corpo grande demais) vira erro.
             */
            ensaiar: (arquivos, q = {}) => this.request("/v1/admin/layouts/ensaio", {
                method: "POST",
                body: corpoDoEnsaio(arquivos),
                // "manda o corpo como está" — o mesmo caminho do upload de CCB. Sem
                // isto o `request` faria `JSON.stringify` de um FormData ("[object
                // FormData]") e o servidor receberia texto vazio.
                isFormData: true,
                query: { ...q },
            }),
            /**
             * O MESMO ensaio, devolvendo o PACOTE PADRÃO em zip (`formato=zip`).
             *
             * É uma segunda ida ao servidor com os mesmos arquivos, e é de propósito:
             * o zip é montado lá (o navegador não escreve zip) e o ensaio não muda
             * estado nenhum — o único rastro é mais uma linha em `admin_layout_ensaios`,
             * que é o registro honesto de que alguém ensaiou de novo.
             */
            ensaiarZip: async (arquivos, q = {}) => {
                const token = await this.storage.getAccess();
                const url = new URL("/v1/admin/layouts/ensaio", this.opts.baseUrl);
                for (const [k, v] of Object.entries({ ...q, formato: "zip" })) {
                    if (v !== undefined && v !== null)
                        url.searchParams.set(k, String(v));
                }
                const res = await this.fetchImpl(url.toString(), {
                    method: "POST",
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: corpoDoEnsaio(arquivos),
                });
                if (!res.ok)
                    throw new Error(`Falha ao baixar o pacote do ensaio (${res.status})`);
                return await res.blob();
            },
            /**
             * A URL do ensaio de saída. Existe para quem já tem o token em mãos (a
             * suíte de navegador); a tela usa `ensaioMovimento`, porque a rota exige
             * `Authorization` e `<a href>` não manda header nenhum.
             */
            ensaioMovimentoUrl: (q) => {
                const url = new URL("/v1/admin/layouts/ensaio/movimento", this.opts.baseUrl);
                for (const [k, v] of Object.entries(q)) {
                    if (v !== undefined && v !== null)
                        url.searchParams.set(k, String(v));
                }
                return url.toString();
            },
            /**
             * O movimento desta unidade NO LAYOUT PEDIDO, sem emitir — "como ficaria
             * o arquivo se eu trocasse o layout?".
             *
             * `formato=json` numa ida só: o arquivo em base64 (para salvar) e o
             * relatório de geração (para ler). Pedir o binário e depois o relatório
             * seriam dois ensaios, dois registros e duas chances de divergirem.
             */
            ensaioMovimento: (q) => this.request("/v1/admin/layouts/ensaio/movimento", {
                query: { ...q, formato: "json" },
            }),
            /** Os ensaios registrados — sem conteúdo, por construção (FR-029). */
            ensaios: (q = {}) => this.request("/v1/admin/layouts/ensaios", { query: { ...q } }),
            daUnidade: (unidadeId, competencia) => this.request(`/v1/admin/prefeituras/${unidadeId}/layouts${competencia ? `?competencia=${encodeURIComponent(competencia)}` : ""}`),
            atribuir: (unidadeId, body) => this.request(`/v1/admin/prefeituras/${unidadeId}/layouts`, {
                method: "POST",
                body,
            }),
            documentoUrl: (codigo, fmt = "pdf") => `${this.opts.baseUrl}/v1/admin/layouts/${encodeURIComponent(codigo)}/layout-tecnico${fmt === "md" ? ".md" : ""}`,
            /**
             * O documento como blob — e' assim que a tela abre.
             *
             * A rota exige Authorization, e `<a href>` nao manda header nenhum. As
             * rotas publicas do padrao (`/v1/prefeitura/folhas/.../layout-tecnico`)
             * podem ser linkadas direto; estas nao, porque o catalogo e' da
             * averbadora e um rascunho nao pode vazar por link.
             */
            documentoBlob: async (codigo, fmt = "pdf") => {
                const token = await this.storage.getAccess();
                const url = new URL(`/v1/admin/layouts/${encodeURIComponent(codigo)}/layout-tecnico${fmt === "md" ? ".md" : ""}`, this.opts.baseUrl).toString();
                const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                if (!res.ok)
                    throw new Error(`Falha ao baixar o documento do layout (${res.status})`);
                return await res.blob();
            },
            /**
             * As provas de ponta a ponta registradas para um layout, deste ambiente.
             *
             * O banco é separado por ambiente, então a API de produção não enxerga o
             * que rodou em homologação — é por isso que a ficha mostra os dois lados:
             * o resultado ao vivo daqui e a referência declarada no catálogo.
             */
            provas: (codigo, q = {}) => {
                const p = new URLSearchParams();
                for (const [k, v] of Object.entries(q))
                    if (v !== undefined && v !== null)
                        p.set(k, String(v));
                const s = p.toString();
                return this.request(`/v1/admin/layouts/${encodeURIComponent(codigo)}/provas${s ? `?${s}` : ""}`);
            },
            /**
             * Monta o cenário de prova — e as três abaixo respondem 403 em produção,
             * por decisão do SERVIDOR, não deste cliente. Quem as chama é o runner da
             * Camada 3 e o `globalSetup` da suíte de navegador; nenhuma tela chama.
             */
            provasPreparar: (body) => this.request("/v1/admin/layouts/provas/preparar", { method: "POST", body }),
            provasRegistrar: (body) => this.request("/v1/admin/layouts/provas/registrar", { method: "POST", body }),
            /** Cancela, encerra, revoga e DESATIVA. Idempotente; nunca apaga. */
            provasEncerrar: (body) => this.request("/v1/admin/layouts/provas/encerrar", {
                method: "POST",
                body,
            }),
        },
        /** Feature 008 — o formato declarado de uma unidade; `null` remove. Devolve a cobertura resultante. */
        formatoDeclarado: (unidadeId, body) => this.request(`/v1/admin/prefeituras/${unidadeId}/formato-declarado`, { method: "PUT", body }),
        me: () => this.request("/v1/admin/me"),
        // ===== IA (OpenAI) =====
        aiConfig: () => this.request("/v1/admin/ai/config"),
        aiSaveKey: (apiKey) => this.request("/v1/admin/ai/config", { method: "PUT", body: { apiKey } }),
        aiClearKey: () => this.request("/v1/admin/ai/config", { method: "DELETE" }),
        aiTest: () => this.request("/v1/admin/ai/test", {
            method: "POST",
        }),
        // ===== SMTP (e-mails de confirmação) =====
        smtpConfig: () => this.request("/v1/admin/smtp/config"),
        smtpSave: (input) => this.request("/v1/admin/smtp/config", { method: "PUT", body: input }),
        smtpClear: () => this.request("/v1/admin/smtp/config", { method: "DELETE" }),
        smtpTest: (to) => this.request("/v1/admin/smtp/test", {
            method: "POST",
            body: { to },
        }),
        // ===== Config de suporte (info exibida ao servidor em Conta > Suporte) =====
        suporteConfig: () => this.request("/v1/admin/suporte-config"),
        suporteSave: (input) => this.request("/v1/admin/suporte-config", { method: "PUT", body: input }),
        // ===== URLs base da API por ambiente (exibidas ao banco em /banco/api) =====
        apiAmbientesConfig: () => this.request("/v1/admin/api-ambientes"),
        apiAmbientesSave: (input) => this.request("/v1/admin/api-ambientes", { method: "PUT", body: input }),
        aiNormalizeCsv: (body) => this.request("/v1/admin/ai/normalize-csv", { method: "POST", body }),
        dashboard: () => this.request("/v1/admin/dashboard"),
        /** Contagem de cadastros do site institucional aguardando aprovacao.
         *  Alimenta os badges do menu lateral. Recurso sem permissao volta 0. */
        cadastrosPendentes: () => this.request("/v1/admin/cadastros-pendentes"),
        /**
         * Bancos parceiros. Sem `limit`, `q` nem `status`, devolve a lista inteira
         * como sempre (regra C1) e `total` vem ausente — por isso ele e opcional
         * aqui, e nao um `number` que mentiria valendo zero.
         */
        /** `incluirTeste: "1"` traz tambem os credores marcados como de teste, que a
         *  listagem esconde por padrao (mesma regra e mesmo formato do `listPrefeituras`). */
        listBancos: (q) => this.request("/v1/admin/bancos", { query: q ?? {} }),
        getBanco: (id) => this.request(`/v1/admin/bancos/${id}`),
        /**
         * Catalogo de verbas do banco — BIBLIOTECA reutilizavel.
         *
         * Nao classifica folha nenhuma: nao diz em qual prefeitura, em qual folha
         * nem desde quando a verba vale. Serve para o cadastro de convenio oferecer
         * candidatas em vez de exigir digitacao. Quem declara e' o convenio.
         */
        listVerbasBanco: (id) => this.request(`/v1/admin/bancos/${id}/verbas`),
        /**
         * A competencia que o servidor abriria para cada verba de desconto da
         * unidade se a declaracao viesse sem competencia (feature 010). E' o que
         * o cadastro de convenio pre-preenche em "Vale a partir de".
         */
        sugestaoVigencia: (unidadeId) => this.request(`/v1/admin/rubricas/sugestao-vigencia?unidadeId=${encodeURIComponent(String(unidadeId))}`),
        /** Substitui a lista inteira. O catalogo e' pequeno e a tela edita tudo de uma vez. */
        replaceVerbasBanco: (id, verbas) => this.request(`/v1/admin/bancos/${id}/verbas`, {
            method: "PUT",
            body: { verbas },
        }),
        upsertBanco: (b) => this.request("/v1/admin/bancos", { method: "POST", body: b }),
        testarBanco: (id) => this.request(`/v1/admin/bancos/${id}/testar-conexao`, {
            method: "POST",
        }),
        deleteBanco: (id, confirm) => this.request(`/v1/admin/bancos/${id}`, { method: "DELETE", body: confirm }),
        /** Recusa um cadastro vindo do site institucional: HARD DELETE do banco e de
         *  todas as pessoas vinculadas (representantes, responsaveis, master).
         *  So aceita banco com status "pendente" — 400 caso contrario. Irreversivel;
         *  o motivo fica gravado na auditoria, unico rastro que sobra. */
        /**
         * Converte um credor PARCEIRO em CONSIGNATARIA sem acesso (#203): tira login,
         * operadores e tokens, arquiva os convenios e mantem os contratos. A
         * justificativa (10+ caracteres) vai para a auditoria.
         */
        converterEmConsignataria: (id, justificativa) => this.request(`/v1/admin/bancos/${id}/tipo`, { method: "PATCH", body: { tipo: "consignataria", justificativa } }),
        recusarCadastroBanco: (id, motivo) => this.request(`/v1/admin/bancos/${id}/recusar-cadastro`, {
            method: "POST",
            body: { motivo },
        }),
        bancoDocumentoBlob: async (id, parte) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/admin/bancos/${id}/documento/${parte}`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao baixar documento banco (${res.status})`);
            return await res.blob();
        },
        /**
         * Os órgãos e entidades cadastrados. Mesma regra C1 do `listBancos`.
         *
         * Sem filtro, devolve tudo — o comportamento que sempre teve. Os filtros
         * (feature 009) são todos opcionais: `q` casa nome, sigla, razão social e
         * CNPJ; os demais são igualdade. `situacao` e `status` são o mesmo recorte
         * por dois nomes: o primeiro é o da 009, resolvido em memória; o segundo é
         * o que o caminho paginado no Postgres entende. A rota aceita os dois.
         *
         * ATENÇÃO ao `total` quando há recorte: ele conta GRUPOS (cada
         * prefeitura-mãe com as unidades dela), não linhas — a tela agrupa por raiz
         * e recortar por linha partiria um grupo entre duas páginas. Ou seja,
         * `prefeituras.length` da página é normalmente MAIOR que o tamanho da
         * página pedido. Ver 007/D6.
         *
         * Um filtro da 009 DESLIGA o caminho paginado: eles dependem de relação
         * vigente e de derivados que o Postgres não conhece, e a rota os resolve em
         * memória. Nesse caso `total` vem ausente, como no C1.
         */
        listPrefeituras: (q) => this.request("/v1/admin/prefeituras", {
            query: q ?? {},
        }),
        /** O catálogo de tipos, esferas, poderes e classificações, com rótulo e
         *  sugestão — para a tela não duplicar a tabela de `@atlas/types`. */
        organizacoesTipos: () => this.request("/v1/admin/organizacoes/tipos"),
        upsertPrefeitura: (p) => this.request("/v1/admin/prefeituras", { method: "POST", body: p }),
        /** Zera a base de prefeituras. Exige senha compartilhada (env
         *  ADMIN_PURGE_PASSWORD). Nao cascatea pra convenios/folhas. */
        limparBasePrefeituras: (senha) => this.request("/v1/admin/prefeituras/limpar-base", {
            method: "POST",
            body: { senha },
        }),
        sincronizarPrefeitura: (id) => this.request(`/v1/admin/prefeituras/${id}/sincronizar`, { method: "POST" }),
        /** Recusa um cadastro vindo do site institucional: HARD DELETE da prefeitura
         *  e de todas as pessoas vinculadas (representantes, responsaveis, master).
         *  So aceita prefeitura com status "pendente" — 400 caso contrario.
         *  Irreversivel; o motivo fica gravado na auditoria. */
        recusarCadastroPrefeitura: (id, motivo) => this.request(`/v1/admin/prefeituras/${id}/recusar-cadastro`, {
            method: "POST",
            body: { motivo },
        }),
        prefeituraDocumentoBlob: async (id, parte) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/admin/prefeituras/${id}/documento/${parte}`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao baixar documento prefeitura (${res.status})`);
            return await res.blob();
        },
        /** Anexos de carga do Passo 6, na ordem de envio. Lista vazia e' o normal:
         *  a etapa e' opcional e cadastro criado pela averbadora nao passa por ela. */
        prefeituraDocumentosCarga: (id) => this.request(`/v1/admin/prefeituras/${id}/documentos-carga`),
        /** Bytes de um anexo de carga. `docId` e' o `id` que a lista devolve — nao o
         *  nome do arquivo, que pode repetir entre dois anexos. */
        prefeituraDocumentoCargaBlob: async (id, docId) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/admin/prefeituras/${id}/documentos-carga/${docId}`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao baixar anexo de carga (${res.status})`);
            return await res.blob();
        },
        // ===== Pessoas do cadastro (representantes / responsaveis / master) =====
        // Sub-recursos de /v1/admin/{prefeituras,bancos}/:id. O primeiro argumento
        // e' sempre o tenant (kind + id) — os dois lados tem o mesmo contrato, entao
        // um metodo so atende prefeitura e banco.
        listRepresentantes: (kind, tenantId) => this.request(`/v1/admin/${kind}/${tenantId}/representantes`),
        /** Sem `repId` cria; com `repId` atualiza aquele representante. */
        salvarRepresentante: (kind, tenantId, r, repId) => this.request(`/v1/admin/${kind}/${tenantId}/representantes${repId ? `/${repId}` : ""}`, { method: "POST", body: r }),
        removerRepresentante: (kind, tenantId, repId) => this.request(`/v1/admin/${kind}/${tenantId}/representantes/${repId}`, {
            method: "DELETE",
        }),
        listResponsaveis: (kind, tenantId) => this.request(`/v1/admin/${kind}/${tenantId}/responsaveis`),
        salvarResponsavel: (kind, tenantId, r, respId) => this.request(`/v1/admin/${kind}/${tenantId}/responsaveis${respId ? `/${respId}` : ""}`, { method: "POST", body: r }),
        removerResponsavel: (kind, tenantId, respId) => this.request(`/v1/admin/${kind}/${tenantId}/responsaveis/${respId}`, {
            method: "DELETE",
        }),
        /** `master` vem null quando o tenant ainda nao tem um — estado normal. */
        getMaster: (kind, tenantId) => this.request(`/v1/admin/${kind}/${tenantId}/master`),
        /** Upsert: a relacao e' 1:1, nao existe "criar um segundo master". */
        salvarMaster: (kind, tenantId, m) => this.request(`/v1/admin/${kind}/${tenantId}/master`, {
            method: "PUT",
            body: m,
        }),
        removerMaster: (kind, tenantId) => this.request(`/v1/admin/${kind}/${tenantId}/master`, { method: "DELETE" }),
        /** Consulta CNPJ via BrasilAPI (Receita + Junta Comercial). Usado nos
         *  modais de cadastro de prefeitura E de banco — endpoint reaproveitado. */
        consultarCnpjPrefeitura: (cnpj) => this.request(`/v1/admin/prefeituras/consulta-cnpj/${encodeURIComponent(cnpj.replace(/\D/g, ""))}`),
        /** Zera a base de bancos. Exige senha compartilhada (env ADMIN_PURGE_PASSWORD). */
        limparBaseBancos: (senha) => this.request("/v1/admin/bancos/limpar-base", {
            method: "POST",
            body: { senha },
        }),
        /** Destrava os endpoints destrutivos (kill-switch) por N segundos. Exige a
         *  senha ADMIN_PURGE_PASSWORD. Usado antes de rodar um delete cirurgico. */
        destravarDestrutivos: (senha, durationSec = 120) => this.request("/v1/admin/destructive-lock/unlock", {
            method: "POST",
            body: { senha, durationSec },
        }),
        /** Apaga servidores por NOME (ex.: "ZZ OBSOLETA - NAO USAR", "EXEMPLO - ..."):
         *  so a ficcao sai, os reais ficam. Requer o kill-switch destravado + senha. */
        deleteServidoresPorNome: (nomes, senha) => this.request("/v1/admin/servidores/delete-by-nome", {
            method: "POST",
            body: { nomes, senha },
        }),
        /** Delete cirurgico por matricula(s). Requer kill-switch destravado + senha
         *  ADMIN_PURGE_PASSWORD. Cascade: remove servidor + contratos + divida herdada
         *  (linhas do jsonb; lote inteiro se ficar vazio) + pre-reservas +
         *  portabilidades + cotacoes telemedicina + beneficio-cliques/intencoes +
         *  servidor_status do PG e da memoria. */
        deleteServidoresPorMatricula: (matriculas, senha) => this.request("/v1/admin/servidores/delete-matriculas", { method: "POST", body: { matriculas, senha } }),
        /**
         * Os sub-usuarios do portal desta organizacao — SOMENTE LEITURA (feature 009).
         *
         * Quem cria, desativa e redefine senha continua sendo o login da propria
         * organizacao (`/v1/prefeitura/perfis`). Isto existe para a averbadora
         * responder "quem tem acesso ao portal desta camara?" sem pedir a senha do
         * cliente. Consumido pela aba "Usuarios e acessos" da ficha.
         */
        /**
         * AS RELACOES INSTITUCIONAIS — quem exerce qual papel por quem (feature 009).
         *
         * NAO ha exclusao, e a ausencia e' o contrato: encerrar e' `alterar` com
         * `competenciaFim`. Apagar destruiria a resposta para "quem respondia por
         * esta folha em marco?", e a folha de marco continua existindo depois que a
         * relacao acaba.
         */
        relacoes: {
            /** `competencia` filtra por VIGENCIA; `"todas"` desliga o filtro. Ausente
             *  = a competencia corrente, que e' a pergunta que a tela faz ao abrir. */
            list: (q) => this.request("/v1/admin/relacoes", {
                query: Object.fromEntries(Object.entries(q ?? {}).filter(([, v]) => v !== undefined && v !== "")),
            }),
            /** `materializadas` vem sempre VAZIO desde a fase D da feature 009
             *  (T058). Enquanto a derivacao existia, criar uma relacao num par que o
             *  vinculo antigo ja cobria gravava os tres papeis de uma vez, e este
             *  campo dizia o que aconteceu alem do pedido. O campo fica: e' contrato
             *  de fio, e a tela le. */
            criar: (body) => this.request("/v1/admin/relacoes", { method: "POST", body }),
            /** Encerrar (`competenciaFim`), corrigir, suspender ou reativar. Encerrar
             *  quem processa a folha com competencia aberta responde `409
             *  confirmacao_necessaria` ate' vir com `confirmar: true`. */
            alterar: (id, patch) => this.request(`/v1/admin/relacoes/${id}`, { method: "PATCH", body: patch }),
        },
        /**
         * A REGRA DE MARGEM da unidade pagadora (ADR-0012, etapa 2).
         *
         * Percentual em FRACAO: 0.4 e' 40%. A tela e' quem formata, e a API recusa
         * qualquer numero acima de 1 — digitar `40` e' o erro mais provavel do
         * formulario, e ele nao pode virar uma margem quarenta vezes maior.
         *
         * NAO ha exclusao: encerrar e' `alterar` com `competenciaFim`.
         */
        regrasMargem: {
            list: (q) => this.request("/v1/admin/regras-margem", {
                query: Object.fromEntries(Object.entries(q ?? {}).filter(([, v]) => v !== undefined)),
            }),
            /**
             * O ENSAIO (etapa 3): o que a regra proposta faria com a folha, medido da
             * fonte autoritativa. Nao grava nada. `medicao_valida: false` vem com o
             * status da causa (404 unidade, 422 exige filial, 503 fonte indisponivel)
             * — nunca 200 com zero.
             */
            ensaiar: (body) => this.request("/v1/admin/regras-margem/ensaio", { method: "POST", body }),
            /**
             * Cadastrar EXIGE o ensaio (ADR-0012 §6), como ligar a trava exige a
             * medicao: `ensaio.medidoEm` com menos de 10 minutos e
             * `vinculosAcimaDoNovoLimite` igual ao que uma nova medicao acusar —
             * senao `422 medicao_desatualizada` / `medicao_divergente`.
             */
            criar: (body) => this.request("/v1/admin/regras-margem", { method: "POST", body }),
            alterar: (id, patch) => this.request(`/v1/admin/regras-margem/${id}`, {
                method: "PATCH",
                body: patch,
            }),
            /** A conferencia da base legal do orgao (#273): base informada x legal por servidor. */
            baseLegal: (unidadeId) => this.request(`/v1/admin/regras-margem/base-legal/${unidadeId}`),
            /** So' regra AGENDADA (que ainda nao valeu). A que ja valeu se encerra
             *  ou se corrige. Recusa `ja_vigorou` / `ja_cancelada` como validacao. */
            cancelar: (id, body) => this.request(`/v1/admin/regras-margem/${id}/cancelar`, {
                method: "POST",
                body,
            }),
        },
        /**
         * A CONSIGNATARIA nos dois sentidos.
         *
         * O ARQUIVO MENSAL (#198): a averbadora sobe em nome dela. Enviar registra e
         * decide as linhas; a previa mostra o que confirmar faria; confirmar exige o
         * `previaHash` da previa vista.
         *
         * O RETORNO (#189): o que a folha do orgao descontou nas rubricas de um
         * credor, gerado do retorno processado. Nao e' movimento.
         */
        consignatarias: {
            /**
             * Credores com rubrica confirmada na unidade — os que podem receber retorno —
             * e, à parte, as rubricas de consignação confirmadas sem credor.
             */
            candidatos: (unidadeId) => this.request(`/v1/admin/consignatarias/candidatos?unidadeId=${unidadeId}`),
            /** Escolhe o formato do arquivo do credor; o padrão volta a "não escolheu". */
            definirLayoutRetorno: (credorId, layoutCodigo) => this.request(`/v1/admin/consignatarias/${credorId}/retorno-layout`, {
                method: "PUT",
                body: { layoutCodigo },
            }),
            /** O retorno de uma ou mais rubricas sem consignatária vinculada, num arquivo só. */
            gerarRetornoSemCredor: (p) => this.request("/v1/admin/consignatarias/sem-credor/retorno", {
                method: "POST",
                body: p,
            }),
            listarRetornosSemCredor: (p) => {
                const qs = new URLSearchParams({ unidadeId: String(p.unidadeId) });
                if (p.competencia)
                    qs.set("competencia", p.competencia);
                return this.request(`/v1/admin/consignatarias/sem-credor/retorno?${qs}`);
            },
            /**
             * Para `downloadAuthed`: o arquivo gerado para rubricas sem credor.
             * `formato: "xlsx"` baixa o mesmo conteúdo como planilha com cabeçalho
             * (só o formato detalhado tem a versão em planilha).
             */
            retornoSemCredorUrl: (id, formato = "txt") => `${this.opts.baseUrl}/v1/admin/consignatarias/sem-credor/retorno/${encodeURIComponent(id)}/arquivo?formato=${formato}`,
            gerarRetorno: (credorId, p) => this.request(`/v1/admin/consignatarias/${credorId}/retorno`, {
                method: "POST",
                body: p,
            }),
            listarRetornos: (credorId, p) => {
                const qs = new URLSearchParams({ unidadeId: String(p.unidadeId) });
                if (p.competencia)
                    qs.set("competencia", p.competencia);
                return this.request(`/v1/admin/consignatarias/${credorId}/retorno?${qs}`);
            },
            /**
             * Para `downloadAuthed`: o arquivo gerado, pelo id do registro na troca
             * de arquivos. `formato: "xlsx"` baixa o mesmo conteúdo como planilha com
             * cabeçalho (só o formato detalhado tem a versão em planilha).
             */
            retornoUrl: (credorId, id, formato = "txt") => `${this.opts.baseUrl}/v1/admin/consignatarias/${credorId}/retorno/${encodeURIComponent(id)}/arquivo?formato=${formato}`,
            /** Liga/desliga a confirmação automática do arquivo mensal deste credor (auditada). */
            definirConfirmacaoAutomatica: (credorId, ativa) => this.request(`/v1/admin/consignatarias/${credorId}/confirmacao-automatica`, { method: "PUT", body: { ativa } }),
            /** A fila: arquivos em prévia de todos os credores e órgãos, do mais antigo ao mais novo. */
            arquivosPendentes: () => this.request("/v1/admin/consignatarias/arquivos/pendentes"),
            enviarArquivo: (credorId, unidadeId, arquivo, nome) => {
                const fd = new FormData();
                fd.append("unidadeId", String(unidadeId));
                fd.append("arquivo", arquivo, nome ?? arquivo.name ?? "arquivo.txt");
                return this.request(`/v1/admin/consignatarias/${credorId}/arquivos`, { method: "POST", body: fd, isFormData: true });
            },
            previa: (credorId, arquivoId) => this.request(`/v1/admin/consignatarias/${credorId}/arquivos/${encodeURIComponent(arquivoId)}/previa`),
            confirmar: (credorId, arquivoId, body) => this.request(`/v1/admin/consignatarias/${credorId}/arquivos/${encodeURIComponent(arquivoId)}/confirmar`, { method: "POST", body }),
            listarArquivos: (credorId, q) => this.request(`/v1/admin/consignatarias/${credorId}/arquivos`, { query: q }),
            linhas: (credorId, arquivoId, q = {}) => this.request(`/v1/admin/consignatarias/${credorId}/arquivos/${encodeURIComponent(arquivoId)}/linhas`, { query: q.situacao?.length ? { situacao: q.situacao.join(",") } : {} }),
            /** O resultado por linha depois do retorno: descontado, parcial, recusado ou ausente. */
            /** #279: `totais` (pedido x descontado x repassado) e os `repasses` do credor na competencia. */
            resultado: (credorId, q) => this.request(`/v1/admin/consignatarias/${credorId}/resultado`, { query: q }),
            /** #280: o resultado agrupado pelo credor final de cada rubrica; rubrica sem credor final fica com a consignataria. */
            repassePorCredorFinal: (credorId, q) => this.request(`/v1/admin/consignatarias/${credorId}/repasse-por-credor-final`, { query: q }),
            /** O repasse por credor final em CSV, como Blob — a rota exige Bearer. */
            repassePorCredorFinalCsv: async (credorId, q) => {
                const token = await this.storage.getAccess();
                const url = new URL(`/v1/admin/consignatarias/${credorId}/repasse-por-credor-final`, this.opts.baseUrl);
                url.searchParams.set("unidadeId", String(q.unidadeId));
                url.searchParams.set("competencia", q.competencia);
                url.searchParams.set("formato", "csv");
                const res = await this.fetchImpl(url.toString(), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                if (!res.ok)
                    throw new Error(`Falha ao exportar o repasse por credor final (${res.status})`);
                return await res.blob();
            },
            /** O resultado em CSV, como Blob — a rota exige Bearer. */
            resultadoCsv: async (credorId, q) => {
                const token = await this.storage.getAccess();
                const url = new URL(`/v1/admin/consignatarias/${credorId}/resultado`, this.opts.baseUrl);
                url.searchParams.set("unidadeId", String(q.unidadeId));
                url.searchParams.set("competencia", q.competencia);
                url.searchParams.set("formato", "csv");
                const res = await this.fetchImpl(url.toString(), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                if (!res.ok)
                    throw new Error(`Falha ao exportar o resultado (${res.status})`);
                return await res.blob();
            },
            /** As linhas em CSV (`;`, UTF-8 com BOM), como Blob — a rota exige Bearer. */
            linhasCsv: async (credorId, arquivoId, situacao = []) => {
                const token = await this.storage.getAccess();
                const url = new URL(`/v1/admin/consignatarias/${credorId}/arquivos/${encodeURIComponent(arquivoId)}/linhas`, this.opts.baseUrl);
                url.searchParams.set("formato", "csv");
                if (situacao.length)
                    url.searchParams.set("situacao", situacao.join(","));
                const res = await this.fetchImpl(url.toString(), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                if (!res.ok)
                    throw new Error(`Falha ao exportar as linhas (${res.status})`);
                return await res.blob();
            },
            // ===== #281: a consignacao operada uma a uma, sem o arquivo do mes.
            /** As consignacoes do credor na unidade, com o valor que desconta na competencia (padrao: a atual). */
            listarConsignacoes: (credorId, q) => this.request(`/v1/admin/consignatarias/${credorId}/consignacoes`, { query: q }),
            historicoConsignacao: (credorId, consignacaoId) => this.request(`/v1/admin/consignatarias/${credorId}/consignacoes/${encodeURIComponent(consignacaoId)}/historico`),
            /** Regras do arquivo: rubrica do credor, nao de credito, comportamento declarado, pessoa no cadastro. 422 com o codigo. */
            incluirConsignacao: (credorId, body) => this.request(`/v1/admin/consignatarias/${credorId}/consignacoes`, { method: "POST", body }),
            /** O valor novo vale a partir de `competenciaInicio`; o anterior termina na competencia de antes. */
            alterarValorConsignacao: (credorId, consignacaoId, body) => this.request(`/v1/admin/consignatarias/${credorId}/consignacoes/${encodeURIComponent(consignacaoId)}/valor`, { method: "PATCH", body }),
            /** `competenciaFim` e' a ULTIMA com desconto. Com `solicitacaoId`, o pedido do servidor (#276) vira atendido. */
            encerrarConsignacao: (credorId, consignacaoId, body) => this.request(`/v1/admin/consignatarias/${credorId}/consignacoes/${encodeURIComponent(consignacaoId)}/encerrar`, { method: "POST", body }),
        },
        // #279: o repasse registrado pela averbadora em nome do orgao.
        repasses: {
            listar: (unidadeId, competencia, credorId) => this.request(`/v1/admin/prefeituras/${unidadeId}/folhas/${competencia}/repasses`, { query: credorId ? { credorId } : undefined }),
            registrar: (unidadeId, competencia, p) => this.request(`/v1/admin/prefeituras/${unidadeId}/folhas/${competencia}/repasses`, { method: "POST", body: formDoRepasse(p), isFormData: true }),
            comprovanteUrl: (unidadeId, competencia, id) => `${this.opts.baseUrl}/v1/admin/prefeituras/${unidadeId}/folhas/${competencia}/repasses/${id}/comprovante`,
        },
        margemInformada: {
            configuracoes: (unidadeId) => this.request("/v1/admin/margem-configuracoes", {
                query: { unidadeId },
            }),
            criar: (body) => this.request("/v1/admin/margem-configuracoes", {
                method: "POST",
                body,
            }),
            corrigir: (id, body) => this.request(`/v1/admin/margem-configuracoes/${id}`, {
                method: "PATCH",
                body,
            }),
            /**
             * Liga a configuracao com efeito real: o saldo informado passa a decidir.
             * Recusa com 422 `ativacao_impedida` e `details.impedimentos`
             * (`ImpedimentoAtivacaoMargem[]`) quando falta foto, categoria etc.
             */
            ativar: (id) => this.request(`/v1/admin/margem-configuracoes/${id}/ativar`, {
                method: "POST",
                body: {},
            }),
            desativar: (id) => this.request(`/v1/admin/margem-configuracoes/${id}/desativar`, {
                method: "POST",
                body: {},
            }),
            resumo: (unidadeId) => this.request("/v1/admin/margem-operacao/resumo", { query: { unidadeId } }),
            importacoes: (q) => this.request("/v1/admin/margem-operacao/importacoes", {
                query: q,
            }),
            importarParte: (body) => this.request("/v1/admin/margem-operacao/importacoes/partes", { method: "POST", body }),
            compromissos: (q) => this.request("/v1/admin/margem-operacao/compromissos", { query: q }),
            bloqueios: (q) => this.request("/v1/admin/margem-operacao/bloqueios", { query: q }),
            divergencias: (q) => this.request("/v1/admin/margem-operacao/divergencias", { query: q }),
            resolverDivergencia: (id, body) => this.request(`/v1/admin/margem-operacao/divergencias/${id}`, { method: "PATCH", body }),
            preverConsignacoes: (body) => this.request("/v1/admin/consignacoes-nao-crediticias/importar", { method: "POST", body: { ...body, confirmar: false } }),
            confirmarConsignacoes: (body, previaHash) => this.request("/v1/admin/consignacoes-nao-crediticias/importar", { method: "POST", body: { ...body, previaHash, confirmar: true } }),
        },
        /**
         * O que vale HOJE para a organizacao, mais o historico.
         *
         * `vigente.padrao` = ninguem cadastrou nada e o sistema aplica 35/5/5. A tela
         * mostra isso por extenso, para o operador nao achar que o padrao foi decisao
         * de alguem.
         */
        regraMargemDaOrganizacao: (id) => this.request(`/v1/admin/prefeituras/${id}/regra-margem`),
        /** Os dois lados de uma organizacao: o que ela exerce e o que ela recebe. */
        relacoesDaOrganizacao: (id) => this.request(`/v1/admin/prefeituras/${id}/relacoes`),
        organizacaoPerfis: (id) => this.request(`/v1/admin/prefeituras/${id}/perfis`),
        /**
         * Exclusao PERMANENTE de organizacao. `?hard=true` e o corpo com o desafio
         * sao obrigatorios juntos: desde 07/09/2026 a rota LE e VERIFICA o par
         * (antes recebia e ignorava). Sem `hard`, a rota desativa e ignora o corpo.
         *
         * O desafio sai de `solicitarConfirmacao` e vale uma vez so.
         */
        deletePrefeitura: (id, confirm) => this.request(`/v1/admin/prefeituras/${id}?hard=true`, { method: "DELETE", body: confirm }),
        /**
         * Step-up por codigo, para acoes destrutivas.
         *
         * Aponta para `/v1/confirmacao/solicitar` — a emissao unica da API. Havia
         * uma segunda em `/v1/admin/confirmacao/solicitar`, removida em 07/09/2026:
         * ela devolvia a constante "000000" e ainda revelava o valor na resposta,
         * entao nao era segundo fator nenhum.
         *
         * Delega a `confirmacao.solicitar` — mesmo endpoint, mesmo tipo. Eram duas
         * declaracoes identicas do mesmo contrato, e duplicata identica e' o
         * mecanismo pelo qual as duas divergem: uma mudanca precisava ser feita
         * duas vezes, e esquecer uma passava no typecheck.
         */
        solicitarConfirmacao: (acao, recurso) => this.confirmacao.solicitar(acao, recurso),
        /**
         * Convenios (banco x prefeitura). Sem `limit` nem filtro, devolve TODOS —
         * ativos e inativos — como sempre (regra C1). E disso que dependem os
         * dropdowns das outras telas.
         *
         * `ativo` tem TRES estados: ausente traz os dois, `true` so ativos,
         * `false` so inativos.
         *
         * `banco_ids`/`prefeitura_ids` sao CSV ("1,2,3"), nao array: a query desta
         * classe e' `Record<string, string|number|boolean>`, sem suporte a lista.
         */
        listConvenios: (q) => this.request("/v1/admin/convenios", { query: q ?? {} }),
        upsertConvenio: (body) => this.request("/v1/admin/convenios", { method: "POST", body }),
        deleteConvenio: (id) => this.request(`/v1/admin/convenios/${id}`, { method: "DELETE" }),
        reativarConvenio: (id) => this.request(`/v1/admin/convenios/${id}/reativar`, { method: "POST" }),
        /** Hard-delete permanente. So convenio INATIVO + sem contratos. */
        deleteConvenioHard: (id) => this.request(`/v1/admin/convenios/${id}/hard`, { method: "DELETE" }),
        getConvenioConfig: (id) => this.request(`/v1/admin/convenios/${id}/config`),
        listConveniosConfigs: () => this.request("/v1/admin/convenios-configs"),
        upsertConvenioConfig: (id, body) => this.request(`/v1/admin/convenios/${id}/config`, {
            method: "POST",
            body,
        }),
        // ID único
        /**
         * Configuracao de ID unico, uma por prefeitura. Sem `limit` nem filtro,
         * devolve a lista inteira (C1).
         *
         * `prefeitura_id` recorta pela CHAVE da linha, nao por campo do jsonb — a
         * config e gravada sob `String(unidadeId)`.
         */
        listIdUnicoConfigs: (q) => this.request("/v1/admin/id-unico/configs", {
            query: q ?? {},
        }),
        upsertIdUnicoConfig: (body) => this.request("/v1/admin/id-unico/configs", {
            method: "POST",
            body,
        }),
        issueIdUnico: (unidadeId) => this.request("/v1/admin/id-unico/issue", {
            method: "POST",
            body: { unidadeId },
        }),
        // Pré-reservas
        /**
         * Pre-reservas (derivadas dos contratos). Sem `limit`, devolve a lista
         * inteira (C1).
         *
         * `banco`/`prefeitura` filtram por NOME — e o que a coluna mostra e o que
         * os chips da tela oferecem. Os `_id` continuam valendo, para quem ja os
         * usava.
         *
         * `resumo` fala da BASE inteira (nao do recorte): sao os cards do topo, que
         * respondem "quantas pre-reservas existem", nao "quantas o filtro trouxe".
         */
        listPreReservas: (q) => this.request("/v1/admin/pre-reservas", { query: q ?? {} }),
        cancelarPreReserva: (id, motivo) => this.request(`/v1/admin/pre-reservas/${id}/cancelar`, {
            method: "POST",
            body: { motivo },
        }),
        sweepPreReservas: () => this.request("/v1/admin/pre-reservas/sweep", { method: "POST" }),
        /**
         * CARGA INICIAL — a base do municipio pelo MESMO par de arquivos do retorno.
         *
         * Substitui `importarTombamento` + o import de servidores em CSV proprio.
         * Consignado sem casamento vira divida herdada com ADF emitida na hora, em
         * vez de quarentena: na primeira carga nao ha remessa contra a qual conferir.
         * Idempotente — reimportar nao duplica nem devolve parcela paga.
         *
         * Manda o zip cru (Blob/ArrayBuffer). Sem `fetch` proprio aqui: `request`
         * cuida de auth e envelope de erro, e o corpo binario passa direto.
         */
        cargaInicial: async (unidadeId, zip, competencia, formato = "retorno") => {
            // `fetch` proprio, mesmo padrao de `enviarRetornoZip`: o `request` faz
            // JSON.stringify no body e nao aceita Content-Type custom. E o 422 aqui e'
            // RECUSA COM MOTIVO — a tela precisa do corpo, entao nao pode virar throw.
            const token = await this.storage.getAccess();
            const url = new URL("/v1/admin/carga-inicial", this.opts.baseUrl);
            url.searchParams.set("unidadeId", String(unidadeId));
            if (competencia)
                url.searchParams.set("competencia", competencia);
            url.searchParams.set("formato", formato);
            const res = await this.fetchImpl(url.toString(), {
                method: "POST",
                headers: {
                    "Content-Type": "application/zip",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: zip,
            });
            const corpo = (await res.json().catch(() => null));
            if (!res.ok && res.status !== 422) {
                const msg = corpo && "error" in corpo ? corpo.error?.message : undefined;
                throw new Error(msg ?? `Falha ao enviar a carga inicial (${res.status})`);
            }
            return corpo;
        },
        // Bate-de-carteira
        /**
         * Bate de carteira (conciliacao banco x folha). O relatorio e CALCULADO a
         * cada chamada — nao ha tabela.
         *
         * `limit`/`offset` vao no CORPO porque a rota e POST e todo o resto do
         * pedido ja mora la. Ausentes = relatorio inteiro (C1).
         *
         * `totalLinhas` e as somas falam do relatorio INTEIRO (sao os cards do
         * topo); `total` e o do recorte, e e ele que pagina.
         *
         * `format: "csv"` NUNCA e recortado, por regra (FR-014/015): um relatorio
         * de conciliacao com 25 das 2000 linhas nao da erro, da um arquivo menor e
         * plausivel que alguem vai conferir contra o extrato do banco.
         */
        bateCarteira: (body) => this.request("/v1/admin/bate-carteira", {
            method: "POST",
            body,
        }),
        bateCarteiraCsvUrl: () => `${this.opts.baseUrl}/v1/admin/bate-carteira`,
        // Catalogo de rubricas — a autoridade sobre o que cada verba da folha
        // significa. `pendentes` e' o numero que trava o go-live: verba nao
        // confirmada nao gera divida, cai em conciliacao.
        /**
         * Trava de margem por unidade pagadora — Fase 6.
         *
         * Ligar muda o que a API responde a um banco: vínculo com pendência passa a
         * receber `margem_pendente_classificacao` em vez de margem. Por isso
         * `ativar` exige `confirmar: true` e os números que o operador viu — a
         * medição precede a decisão, e fica provada.
         */
        /**
         * Pedidos de cancelamento do servidor sobre consignacoes facultativas (#276).
         * Responder REGISTRA o que a consignataria fez; nao encerra o desconto.
         */
        consignacaoSolicitacoes: {
            listar: (q) => this.request("/v1/admin/consignacoes/solicitacoes", { query: q }),
            responder: (id, body) => this.request(`/v1/admin/consignacoes/solicitacoes/${id}/responder`, {
                method: "POST",
                body,
            }),
            /** Pedidos esperando resposta em todos os orgaos — o badge do menu. */
            contagem: () => this.request("/v1/admin/consignacoes/solicitacoes/contagem"),
            prazo: (unidadeId) => this.request(`/v1/admin/consignacoes/solicitacoes/prazo/${unidadeId}`),
            definirPrazo: (unidadeId, prazoRespostaDias) => this.request(`/v1/admin/consignacoes/solicitacoes/prazo/${unidadeId}`, {
                method: "PUT",
                body: { prazoRespostaDias },
            }),
            /** #282: a exigencia de autorizacao do servidor no orgao. */
            autorizacaoConfig: (unidadeId) => this.request(`/v1/admin/consignacoes/autorizacao-config/${unidadeId}`),
            definirAutorizacaoConfig: (unidadeId, body) => this.request(`/v1/admin/consignacoes/autorizacao-config/${unidadeId}`, {
                method: "PUT",
                body,
            }),
        },
        trava: {
            /** Medição da unidade. É o que a tela mostra ANTES de habilitar o botão. */
            impacto: (unidadeId) => this.request("/v1/admin/conciliacao/impacto-margem", {
                query: { unidadeId },
            }),
            /**
             * O que está em QUARENTENA na folha, por verba, ordenado pelo valor.
             *
             * Le do Postgres, nao dos stores em memoria: e' medicao de bloqueio
             * financeiro, e reiniciar a API nao pode mudar a resposta. Fonte
             * indisponivel devolve `medicao_valida: false`, NUNCA zero — zero e'
             * indistinguivel de folha limpa e levaria alguem a concluir que nao ha
             * nada a resolver.
             */
            pendencias: (unidadeId) => this.request("/v1/admin/conciliacao/pendencias", {
                query: { unidadeId },
            }),
            /**
             * Painel pós-ativação, somente leitura.
             *
             * As recusas saem do PostgreSQL (`admin_auditoria`), com filtro e
             * paginação feitos no banco — `total` é o total do filtro, não o tamanho
             * da página.
             */
            monitor: (filtro) => this.request("/v1/admin/conciliacao/trava/monitor", {
                query: Object.fromEntries(Object.entries(filtro ?? {}).filter(([, v]) => v !== undefined && v !== "")),
            }),
            definir: (unidadeId, body) => this.request(`/v1/admin/conciliacao/trava/${unidadeId}`, {
                method: "POST",
                body,
            }),
        },
        rubricas: {
            list: (unidadeId) => this.request("/v1/admin/rubricas", { query: { unidadeId } }),
            confirmar: (body) => this.request("/v1/admin/rubricas/confirmar", { method: "POST", body }),
            /** Comportamento declarado das consignacoes nao crediticias: as vigentes
             *  em `competencia`, ou o historico inteiro sem ela. */
            comportamentos: (unidadeId, competencia) => this.request("/v1/admin/rubricas/comportamentos", {
                query: { unidadeId, competencia },
            }),
            /** Muda o comportamento a partir de uma competencia, sem reclassificar a rubrica. */
            declararComportamento: (body) => this.request("/v1/admin/rubricas/comportamentos", {
                method: "PUT",
                body,
            }),
            /**
             * Troca so' a descricao da rubrica — o `DESCRUB` do movimento. Nao
             * reclassifica nem mexe na vigencia. O proximo retorno com outro texto
             * para a verba substitui este, como sempre.
             */
            alterarDescricao: (id, descricao) => this.request(`/v1/admin/rubricas/${encodeURIComponent(id)}/descricao`, {
                method: "POST",
                body: { descricao },
            }),
            /**
             * As obrigacoes legadas de uma folha — as dividas que o catalogo cunhou.
             *
             * Sob a permissao `rubricas`, nao `contratos`: quem classifica a rubrica
             * precisa ver o que a classificacao dele gerou.
             */
            /**
             * Obrigacoes legadas de uma folha. Sem `limit` nem filtro, devolve a
             * lista inteira (C1).
             *
             * `provisorias` e `comContratoOficial` falam da FOLHA inteira, nao do
             * recorte: sao indicadores de saude da carteira, e encolhe-los junto com
             * o filtro faria a divida tecnica parecer menor a cada clique.
             */
            obrigacoes: (unidadeId, q) => this.request("/v1/admin/rubricas/obrigacoes", { query: { unidadeId, ...(q ?? {}) } }),
            /** O historico mensal de uma obrigacao. So leitura, sem escrita par. */
            historicoObrigacao: (adf) => this.request(`/v1/admin/rubricas/obrigacoes/${encodeURIComponent(adf)}/historico`),
            /** Resumo de conciliacao da competencia, derivado do estado. */
            conciliacaoResumo: (unidadeId, competencia) => this.request("/v1/admin/rubricas/conciliacao/resumo", {
                query: { unidadeId, competencia },
            }),
            /**
             * INATIVA a rubrica encerrando a vigencia dela na competencia informada.
             *
             * Nao existe DELETE, e nao e' timidez: a rubrica classificou descontos que
             * a prefeitura ja processou e repassou. Apagar a linha deixaria aqueles
             * meses sem resposta sobre de quem era o dinheiro. Encerrar diz "daqui pra
             * frente nao vale mais" e preserva o antes.
             */
            encerrarVinculo: (id, competenciaFinal) => this.request(`/v1/admin/rubricas/vinculos/${encodeURIComponent(id)}/encerrar`, { method: "POST", body: { competenciaFinal } }),
            /** Sem `mapa`, e' SECO: devolve as folhas do relatorio pra averbadora
             *  ligar cada uma ao seu unidadeId antes de aplicar. */
            conciliacao: (body) => this.request("/v1/admin/rubricas/conciliacao", { method: "POST", body }),
        },
        // Auditoria
        /**
         * Trilha de auditoria. DOIS MODOS, e a diferenca importa:
         *
         * - Sem `offset`: `limit` e uma JANELA ("os N mais recentes", default 200,
         *   teto 500), servida do cache in-memory fundido com o Postgres. Nao ha
         *   `total` — o fim da lista e o fim da janela, nao o fim da trilha.
         * - Com `offset`: pagina de verdade, lida PURA do Postgres, com `total` do
         *   conjunto filtrado. Uma entrada recem-gravada pode demorar alguns
         *   segundos para aparecer; e o preco de a contagem de paginas nao mentir.
         *
         * Paginar dentro da janela seria o pior dos dois: o operador viraria
         * paginas acreditando ter visto tudo. Ver 007/D4.
         */
        // `acao` e `unidadeId` sao os dois filtros que a ficha de layout usa para
        // pedir a trilha de UMA unidade. Estreitam a busca ao FATO, e a rota ja os
        // aceita nos dois caminhos — inclusive no paginado, desde o merge de 006.
        listAuditoria: (q) => this.request("/v1/admin/auditoria", { query: q ?? {} }),
        // Cotacoes de telemedicina solicitadas pelos servidores (banner de Beneficios).
        // A averbadora ve os dados do servidor — principalmente o telefone — pra formalizar.
        listTelemedicinaCotacoes: () => this.request("/v1/admin/telemedicina/cotacoes"),
        /** `valores` = o que foi formalizado com o servidor. Omitidos, valem os
         *  padroes do plano (R$ 50,00 x 12). Antes o endpoint nao aceitava corpo e
         *  a parcela sempre voltava pro padrao. */
        ativarCotacaoTelemedicina: (id, valores) => this.request(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/ativar`, { method: "POST", body: valores ?? {} }),
        cancelarCotacaoTelemedicina: (id) => this.request(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/cancelar`, { method: "POST" }),
        purgeTelemedicinaCotacoes: () => this.request("/v1/admin/telemedicina/cotacoes/purge", {
            method: "POST",
        }),
        /** Anexa o contrato da telemedicina (R2). Sem ele o plano nao pode ser ativado. */
        uploadContratoTelemedicina: (id, file) => {
            const fd = new FormData();
            fd.append("file", file);
            return this.request(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/contrato`, { method: "POST", body: fd, isFormData: true });
        },
        /** Remove o contrato anexado (arquivo errado) — volta a bloquear a ativacao. */
        removerContratoTelemedicina: (id) => this.request(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/contrato`, {
            method: "DELETE",
        }),
        /** Baixa o contrato anexado da cotacao (envia Authorization). */
        fetchContratoTelemedicinaBlob: async (id) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/admin/telemedicina/cotacoes/${encodeURIComponent(id)}/contrato`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao baixar contrato (${res.status})`);
            return await res.blob();
        },
        // Templates de TERMOS (aceite in-app do servidor + anuencia da prefeitura)
        listTermos: () => this.request("/v1/admin/termos"),
        upsertTermo: (tipo, patch) => this.request(`/v1/admin/termos/${tipo}`, { method: "POST", body: patch }),
        // Perfis admin
        /**
         * Usuarios da averbadora. Sem `limit` nem filtro, devolve todos (regra C1).
         *
         * `perfis` e `presets` NAO sao recortados: eles descrevem o cadastro
         * inteiro (sao o que o modal oferece para escolher), e encolhe-los junto
         * com a pagina deixaria o dropdown sem opcoes.
         */
        listPerfisAdmin: (q) => this.request("/v1/admin/perfis", { query: q ?? {} }),
        upsertPerfilAdmin: (body) => this.request("/v1/admin/perfis", { method: "POST", body }),
        perfilPresetsAdmin: () => this.request("/v1/admin/perfil-presets"),
        criarPerfilPresetAdmin: (body) => this.request("/v1/admin/perfil-presets", { method: "POST", body }),
        rotate2FA: (id) => this.request(`/v1/admin/perfis/${id}/2fa/rotate`, {
            method: "POST",
        }),
        disable2FA: (id) => this.request(`/v1/admin/perfis/${id}/2fa/disable`, { method: "POST" }),
        deletePerfilAdmin: (id) => this.request(`/v1/admin/perfis/${id}`, { method: "DELETE" }),
        reativarPerfilAdmin: (id) => this.request(`/v1/admin/perfis/${id}/reativar`, { method: "POST" }),
        // ===== ADF — averbadora aplica/reporta falha (prefeitura so recebe) =====
        adfCompetencias: () => this.request("/v1/admin/adf/competencias"),
        /**
         * ADFs de uma competencia. Sem `limit` nem filtro, devolve a lista inteira (C1).
         *
         * `contagemStatus` e `contagemOrigem` usam a semantica `exceto` (FR-010):
         * cada uma conta com todos os outros filtros aplicados menos o da propria
         * dimensao. `facetas` descreve a competencia inteira — sao as opcoes dos
         * selects, que nao podem encolher junto com a selecao.
         *
         * `idsSelecionaveis` sao as ADFs `recebida` do RECORTE INTEIRO. E o que faz
         * "marcar o filtro todo" sobreviver a paginacao: sem ele a tela so
         * conseguiria marcar a pagina, e fechar uma competencia viraria virar
         * pagina marcando 25 por vez.
         */
        adfList: (q) => this.request("/v1/admin/adf", { query: q ?? {} }),
        confirmarAdfAdmin: (ids) => this.request("/v1/admin/adf/confirmar", { method: "POST", body: { ids } }),
        reportarFalhaAdfAdmin: (ids, motivo) => this.request("/v1/admin/adf/falha", { method: "POST", body: { ids, motivo } }),
        // ===== Beneficios / descontos por prefeitura =====
        beneficios: {
            list: () => this.request("/v1/admin/beneficios"),
            upsert: (body) => this.request("/v1/admin/beneficios", { method: "POST", body }),
            pausar: (id) => this.request(`/v1/admin/beneficios/${id}/pausar`, { method: "PATCH" }),
            reativar: (id) => this.request(`/v1/admin/beneficios/${id}/reativar`, {
                method: "PATCH",
            }),
            /** Interessados (servidores que clicaram no botao Acessar). ?beneficioId filtra. */
            interessados: (beneficioId) => this.request("/v1/admin/beneficios/interessados", { query: beneficioId ? { beneficioId } : {} }),
            /** Contagem de interessados por beneficio (badge nas listas). */
            interessadosResumo: () => this.request("/v1/admin/beneficios/interessados/resumo"),
        },
        // ===== Modelos de e-mail (editaveis pela averbadora) =====
        emailTemplates: {
            list: () => this.request("/v1/admin/email-templates"),
            upsert: (body) => this.request("/v1/admin/email-templates", { method: "POST", body }),
            remover: (id) => this.request(`/v1/admin/email-templates/${id}`, { method: "DELETE" }),
            /** Envia um teste real via SMTP configurado. Se `vars` for omitido/vazio,
             *  o backend preenche automaticamente com dados de exemplo realistas. */
            enviarTeste: (id, body) => this.request(`/v1/admin/email-templates/${id}/test`, { method: "POST", body }),
            /** Pre-preenchimento de variaveis com dados realistas — usado pelo
             *  modal de teste pra popular os inputs sem o operador digitar. */
            previewVars: (id) => this.request(`/v1/admin/email-templates/${id}/preview-vars`),
        },
        // `limit` ausente devolve a lista inteira, como sempre — a mudanca e
        // ADITIVA. Ver contracts/recorte-listagem.md (regra C1).
        listServidores: (q) => this.request("/v1/admin/servidores", {
            query: q ?? {},
        }),
        updateServidor: (matricula, body) => this.request(`/v1/admin/servidores/${matricula}`, {
            method: "PATCH",
            body,
        }),
        /** Margem e contratos de UMA matrícula — a ficha só-leitura (👁) da tela de
         *  servidores. `prefeituraId` é obrigatório porque matrícula é numerada por
         *  folha: sem ele a rota recusa por ambiguidade em vez de adivinhar. */
        servidorDetalhe: (matricula, prefeituraId) => this.request(`/v1/admin/servidores/${encodeURIComponent(matricula)}/detalhe`, {
            query: { prefeitura_id: prefeituraId },
        }),
        /** Le a config de campos de servidor da prefeitura (cria default se ainda nao existe). */
        getServidorCamposConfig: (unidadeId) => this.request(`/v1/admin/servidores/campos-config/${unidadeId}`),
        /** Salva a config de campos. Backend re-injeta cpf/matricula/email como travados. */
        updateServidorCamposConfig: (unidadeId, campos) => this.request(`/v1/admin/servidores/campos-config/${unidadeId}`, {
            method: "PUT",
            body: { campos },
        }),
        /**
         * Folhas de todas as prefeituras. Sem `limit` nem filtro, devolve a lista
         * inteira (C1).
         *
         * `contagemStatus` usa a semantica `exceto` (FR-010): conta dentro do
         * recorte de prefeitura + competencia, SEM o corte de status. `facetas`
         * descreve a BASE (as opcoes que os filtros oferecem), e `totalBase` e o
         * denominador do "X de Y folha(s)".
         */
        listFolhas: (q) => this.request("/v1/admin/folhas", { query: q ?? {} }),
        upsertFolha: (f) => this.request("/v1/admin/folhas", { method: "POST", body: f }),
        /**
         * O CORTE: emite e congela o movimento da competencia.
         *
         * Antes o movimento so existia se a prefeitura baixasse, e cada download
         * gerava um arquivo do estado daquele instante. Agora e um ato com autor e
         * momento, e o download passa a servir este arquivo.
         *
         * `motivo` e OBRIGATORIO quando ja houve emissao: reemitir e uma
         * RETIFICACAO, e as duas ficam no historico ligadas por `retificacaoDe`.
         */
        emitirMovimento: (folhaId, motivo) => this.request(`/v1/admin/folhas/${encodeURIComponent(folhaId)}/emitir-movimento`, {
            method: "POST",
            body: motivo ? { motivo } : {},
        }),
        /**
         * Sobrescreve o instante do corte desta competencia, com MOTIVO.
         *
         * Para o que a regra nao preve: determinacao dos gestores do orgao, ou
         * feriado municipal (o calculo de dia util so conhece fim de semana).
         * Recusado depois da emissao — o arquivo ja saiu, e mover o corte nao muda
         * o que foi entregue.
         */
        sobrescreverCorte: (folhaId, em, motivo) => this.request(`/v1/admin/folhas/${encodeURIComponent(folhaId)}/corte`, {
            method: "PATCH",
            body: { em, motivo },
        }),
        /**
         * Consolida a competencia — o momento em que o valor vira final.
         *
         * `confirmarIncompleto` e obrigatorio quando falta parte declarada do retorno.
         * Sem ele a API RECUSA e diz o que falta (parte declarada, ou arquivo recebido
         * e nao processado). Nao e conveniencia: consolidar
         * sem uma parte a presume como descontada, o que libera margem de divida viva
         * e atribui repasse a quem nao recebeu. A decisao e da averbadora, mas fica
         * registrada como decisao — nao como clique distraido.
         */
        consolidarFolha: (id, opcoes) => this.request(`/v1/admin/folhas/${id}/consolidar${opcoes?.confirmarIncompleto ? "?confirmar_incompleto=true" : ""}`, { method: "POST" }),
        /**
         * PROCESSA um retorno recebido. E aqui que o dinheiro se move.
         *
         * O upload da prefeitura guarda e valida; este ato aplica: grava resultados,
         * avanca parcela, interrompe contrato de servidor desligado e libera margem.
         * Duas maos, dois momentos — antes tudo acontecia no request de quem enviou.
         *
         * Recusado se o arquivo ja foi processado: dar baixa duas vezes na mesma
         * parcela e o erro caro.
         */
        processarRetorno: (arquivoId) => this.request(`/v1/admin/retornos/${encodeURIComponent(arquivoId)}/processar`, { method: "POST" }),
        deleteFolha: (id) => this.request(`/v1/admin/folhas/${id}`, { method: "DELETE" }),
        // Visao global de contratos averbados (ADF averbadora — todos os bancos).
        // `limit` ausente devolve a lista inteira, como sempre (regra C1).
        /**
         * Contratos de todos os bancos. Sem `limit`, devolve a lista inteira (C1).
         *
         * `facetas` e `totais` falam do RECORTE (filtrado), nunca da pagina — e por
         * isso vem da API: depois do recorte, o servidor e o unico lado que ainda
         * tem o recorte inteiro para somar.
         */
        /**
         * A averbadora informa valor solicitado e taxa de um contrato LEGADO — a
         * EXCEÇÃO da feature 012 (credor sem acesso, ou correção), por isso com
         * `motivo` obrigatório (mín. 10 caracteres). O caminho normal é o banco
         * credor, no portal dele.
         */
        informarCronogramaLegado: (adf, body) => this.request(`/v1/admin/contratos/${encodeURIComponent(adf)}/cronograma`, { method: "POST", body }),
        informarDadosLegado: (adf, body) => this.request(`/v1/admin/contratos/${adf}/dados-financeiros`, {
            method: "POST",
            body,
        }),
        historicoPagamentosContrato: (adf) => this.request(`/v1/admin/contratos/${encodeURIComponent(adf)}/historico-pagamentos`),
        contratos: (q) => this.request("/v1/admin/contratos", { query: q ?? {} }),
        listComunicados: () => this.request("/v1/admin/comunicados"),
        upsertComunicado: (body) => this.request("/v1/admin/comunicados", { method: "POST", body }),
        moveComunicado: (id, direction) => this.request(`/v1/admin/comunicados/${id}/mover`, {
            method: "POST",
            body: { direction },
        }),
        /** Reordena a lista inteira em um POST — usado pelo drag-and-drop. */
        reordenarComunicados: (ids) => this.request("/v1/admin/comunicados/reordenar", {
            method: "POST",
            body: { ids },
        }),
        deleteComunicado: (id) => this.request(`/v1/admin/comunicados/${id}`, { method: "DELETE" }),
        health: () => this.request("/v1/admin/health"),
        impersonateServidor: (matricula) => this.request(`/v1/admin/impersonate/servidor/${encodeURIComponent(matricula)}`, { method: "POST", body: {} }),
        verify: (opts) => this.request("/v1/admin/verify", { query: opts?.fresh ? { fresh: "1" } : {} }),
        logs: (q) => this.request("/v1/admin/logs", { query: q ?? {} }),
        listVitrine: () => this.request("/v1/admin/vitrine"),
        upsertBanner: (b) => this.request("/v1/admin/vitrine", { method: "POST", body: b }),
        // === API tokens ===
        listApiTokens: (filter) => this.request("/v1/admin/api-tokens", { query: filter ?? {} }),
        createApiToken: (body) => this.request("/v1/admin/api-tokens", {
            method: "POST",
            body,
        }),
        // Pausa/reativa UM token (manual, reversível). Não apaga nem toca no perfil/parceria dono.
        pauseApiToken: (id, paused) => this.request(`/v1/admin/api-tokens/${id}/pause`, {
            method: "PATCH",
            body: { paused },
        }),
        /**
         * Uso da API externa do banco, agregado por dia. Não é faturamento de
         * comissão — aquele continua em `banco.relatorioFaturamento`.
         */
        usoApi: (q) => this.request("/v1/admin/api-uso", { query: q ?? {} }),
        // === Webhooks ===
        listWebhooks: (environment) => this.request("/v1/admin/webhooks", {
            query: environment ? { environment } : {},
        }),
        createWebhook: (body) => this.request("/v1/admin/webhooks", {
            method: "POST",
            body,
        }),
        // Webhooks não são apagados/pausados manualmente: seguem o status do banco dono (cascade).
        webhookDeliveries: (id) => this.request(`/v1/admin/webhooks/${id}/deliveries`),
        fireWebhook: (body) => this.request("/v1/admin/webhooks/fire", { method: "POST", body }),
        testWebhook: (id) => this.request(`/v1/admin/webhooks/${id}/test`, { method: "POST" }),
        // === CSV import ===
        importCsv: (entity, csv, params) => this.request(`/v1/admin/${entity}/importar`, {
            method: "POST",
            body: { csv },
            query: params,
        }),
        csvTemplateUrl: (entity) => `${this.opts.baseUrl}/v1/admin/${entity}/csv-template`,
    };
    // ============ Prefeitura (portal completo) ============
    prefeitura = {
        /** O portal serve qualquer órgão ou entidade: `tipo`, `rotuloTipo` e
         *  `nomeExibicao` (feature 009) são o que o cabeçalho usa para se apresentar
         *  pelo que a organização é, em vez de chamá-la de prefeitura. */
        me: () => this.request("/v1/prefeitura/me"),
        /** Manual de uso do portal (PDF) como Blob. A rota exige Authorization —
         *  por isso não há URL para `<a href>`: o manual não é público. */
        manualBlob: () => this.baixarManual("/v1/prefeitura/manual"),
        getConfig: (opts) => this.request("/v1/prefeitura/config", { query: queryDaUnidade(opts) }),
        getServidorCamposConfig: (opts) => this.request("/v1/prefeitura/servidor-campos-config", {
            query: queryDaUnidade(opts),
        }),
        setConfig: (body, opts) => this.request("/v1/prefeitura/config", {
            method: "POST",
            body,
            query: queryDaUnidade(opts),
        }),
        dashboard: (opts) => this.request("/v1/prefeitura/dashboard", { query: queryDaUnidade(opts) }),
        // Servidores (passos 3 e 6)
        // `facetas` = os valores de vinculo/situacao que existem no ACERVO da
        // unidade, nao na pagina. Sao os chips de filtro da tela, e vem do dado
        // porque esses dois campos sao strings livres (o import por CSV faz cast,
        // nao validacao). CONTADAS com semantica `exceto` (FR-010) desde
        // 04/09/2026 — ver `filtrosServidores` na API.
        //
        // `opts` continua ao lado do `query`: o recorte (006) e a unidade ativa
        // (009) sao ortogonais, e `queryDaUnidade` junta os dois na mesma query
        // string. Sem ele, uma responsavel com varias atendidas listaria sempre a
        // primeira.
        servidores: (query, opts) => this.request("/v1/prefeitura/servidores", {
            query: queryDaUnidade(opts, query),
        }),
        editarServidor: (matricula, patch, opts) => this.request(`/v1/prefeitura/servidores/${matricula}`, {
            method: "PATCH",
            body: patch,
            query: queryDaUnidade(opts),
        }),
        // AQUI HAVIA TRES CONSTRUTORES DE URL PARA ROTAS QUE NAO EXISTEM, e eles
        // sairam em 07/09/2026 (item 4 do PENDENCIAS.md): `servidoresCsvTemplateUrl`
        // (/v1/prefeitura/servidores/csv-template), `servidoresLayoutTecnicoUrl` e
        // `servidoresLayoutTecnicoMdUrl` (.../layout-tecnico e .md).
        //
        // A camada prefeitura serve QUATRO rotas de servidor — `campos-config`, a
        // listagem, o `PATCH /:matricula` e `/exportar` — e nunca teve as outras
        // tres. Ninguem os chamava; se alguem chamasse, o browser abriria um 404.
        // Construtor de URL nao passa por `request()`, entao nao havia tipo nem
        // teste para acusar: so sumindo o problema aparece.
        //
        // Se voltarem, as rotas vem antes, e por unidade — e resolvendo que "layout
        // tecnico" hoje ja nomeia outra coisa no portal
        // (`/v1/prefeitura/layouts/:codigo/layout-tecnico`, o de remessa/retorno da
        // ADR-0007).
        /** Campos de servidor configurados pela averbadora pra esta prefeitura —
         *  usado pra montar o cabecalho do CSV (download + import) dinamicamente. */
        servidorCamposConfig: (opts) => this.request("/v1/prefeitura/servidores/campos-config", {
            query: queryDaUnidade(opts),
        }),
        /** Margem e contratos de UMA matrícula — a ficha só-leitura (👁) da tela de
         *  servidores. A unidade sai do escopo do login (`queryDaUnidade`); uma
         *  matrícula de outra folha responde 404, igual a inexistente. */
        servidorDetalhe: (matricula, opts) => this.request(`/v1/prefeitura/servidores/${encodeURIComponent(matricula)}/detalhe`, {
            query: queryDaUnidade(opts),
        }),
        /**
         * AS ORGANIZACOES QUE ESTE LOGIN ALCANCA, e com que papeis.
         *
         * Alimenta o seletor do cabecalho. `competencia` existe porque quem processa
         * a folha de agosto envia o retorno dela em setembro: o seletor da Troca de
         * Arquivos precisa mostrar quem respondia por AQUELA competencia.
         *
         * `partesDoRetorno` vazio = a unidade manda o retorno num arquivo so'.
         */
        unidades: (opts) => this.request("/v1/prefeitura/unidades", {
            query: opts?.competencia ? { competencia: opts.competencia } : {},
        }),
        // Folha (passo 4)
        /** `unidadeId` escolhe uma FILIAL do login; omitido = a propria prefeitura. */
        folhas: (opts) => this.request(opts?.unidadeId ? `/v1/prefeitura/folhas?unidadeId=${idDaUnidade(opts)}` : "/v1/prefeitura/folhas"),
        /** #282: as autorizacoes do servidor na competencia — sem CPF. */
        autorizacoes: {
            listar: (competencia, opts) => this.request("/v1/prefeitura/consignacoes/autorizacoes", {
                query: { competencia, ...(opts?.unidadeId ? { unidadeId: idDaUnidade(opts) } : {}) },
            }),
            csvUrl: (competencia, opts) => `${this.opts.baseUrl}/v1/prefeitura/consignacoes/autorizacoes?competencia=${competencia}&formato=csv${opts?.unidadeId ? `&unidadeId=${idDaUnidade(opts)}` : ""}`,
        },
        /** #279: o repasse aos credores da competencia. Le quem atende a unidade;
         *  registra quem processa a folha ou responde pelo repasse. */
        repasses: {
            listar: (competencia, opts) => 
            /** `credores`: a quem a unidade repassa na competencia (convenios, vinculos de rubrica, repasses ja feitos). */
            this.request(`/v1/prefeitura/folhas/${competencia}/repasses`, {
                query: {
                    ...(opts?.unidadeId ? { unidadeId: idDaUnidade(opts) } : {}),
                    ...(opts?.credorId ? { credorId: opts.credorId } : {}),
                },
            }),
            registrar: (competencia, p, opts) => this.request(`/v1/prefeitura/folhas/${competencia}/repasses${opts?.unidadeId ? `?unidadeId=${idDaUnidade(opts)}` : ""}`, { method: "POST", body: formDoRepasse(p), isFormData: true }),
            comprovanteUrl: (competencia, id, opts) => `${this.opts.baseUrl}/v1/prefeitura/folhas/${competencia}/repasses/${id}/comprovante${opts?.unidadeId ? `?unidadeId=${idDaUnidade(opts)}` : ""}`,
        },
        /** `unidadeId` no CORPO: e' a unica escrita do portal que ja o aceitava
         *  assim (T082), e ha cliente em producao mandando. */
        abrirFolha: (body) => this.request("/v1/prefeitura/folhas", { method: "POST", body }),
        atualizarFolha: (id, body) => this.request(`/v1/prefeitura/folhas/${id}`, { method: "PATCH", body }),
        excluirFolha: (id) => this.request(`/v1/prefeitura/folhas/${id}`, { method: "DELETE" }),
        // Ciclo remessa/retorno (passo 4) — substitui a Movimentacao de folha, que
        // era arquivo proprio e virou um tipo de registro dentro do retorno.
        // Ver specs/adrs/0001-ciclo-remessa-retorno.md.
        resultadosDaFolha: (folhaId) => this.request(`/v1/prefeitura/folhas/${folhaId}/resultados`),
        /** Numeros que a prefeitura confere antes de baixar a remessa, incluindo o
         *  subtotal POR BANCO — e' por banco que ela faz o repasse. */
        remessaResumo: (competencia, opts) => this.request(`/v1/prefeitura/folhas/${competencia}/remessa/resumo`, {
            query: queryDaUnidade(opts),
        }),
        /**
         * Download do MOVIMENTO da competencia — o arquivo que a prefeitura importa
         * no sistema de folha.
         *
         * Rota autenticada, entao a tela busca com o token e cria o blob; nao da pra
         * usar <a href> direto. O corpo vem em latin1 (o layout e de largura fixa em
         * CARACTERES), entao NAO leia como texto UTF-8 no meio do caminho.
         */
        movimentoUrl: (competencia, opts) => `${this.opts.baseUrl}/v1/prefeitura/folhas/${competencia}/movimento` +
            (opts?.layout === "atlas" ? "?layout=atlas" : "") +
            sufixoDaUnidade(opts, opts?.layout === "atlas"),
        /**
         * Documento tecnico de um layout que ESTA unidade fala.
         *
         * O padrao Atlas sempre responde; um codigo adaptado so' se ja tiver sido
         * atribuido a alguma unidade do login — senao, `404`. O catalogo inteiro e'
         * da averbadora.
         */
        layoutDocumentoUrl: (codigo, fmt = "pdf") => `${this.opts.baseUrl}/v1/prefeitura/layouts/${encodeURIComponent(codigo)}/layout-tecnico${fmt === "md" ? ".md" : ""}`,
        /** O mesmo documento como blob — a rota exige token, `<a href>` nao manda. */
        layoutDocumentoBlob: async (codigo, fmt = "pdf") => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/prefeitura/layouts/${encodeURIComponent(codigo)}/layout-tecnico${fmt === "md" ? ".md" : ""}`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao baixar o documento do layout (${res.status})`);
            return await res.blob();
        },
        /**
         * O ENSAIO DE ENTRADA da própria unidade — "este arquivo passa?".
         *
         * Mesma resposta do ensaio da averbadora, com uma diferença: aqui NÃO se
         * escolhe layout. Os candidatos são o vigente da unidade e o padrão Atlas,
         * e a unidade sai do login (ou do lote informado em `unidadeId`) — o
         * catálogo inteiro é da averbadora, e ensaiar contra o layout de outro ente
         * não é pergunta que a prefeitura tenha.
         */
        layoutsEnsaiar: (arquivos, q = {}) => this.request("/v1/prefeitura/layouts/ensaio", {
            method: "POST",
            body: corpoDoEnsaio(arquivos),
            isFormData: true,
            query: { ...q },
        }),
        /** O mesmo ensaio devolvendo o pacote padrão em zip. Ver `admin.layouts.ensaiarZip`. */
        layoutsEnsaiarZip: async (arquivos, q = {}) => {
            const token = await this.storage.getAccess();
            const url = new URL("/v1/prefeitura/layouts/ensaio", this.opts.baseUrl);
            for (const [k, v] of Object.entries({ ...q, formato: "zip" })) {
                if (v !== undefined && v !== null)
                    url.searchParams.set(k, String(v));
            }
            const res = await this.fetchImpl(url.toString(), {
                method: "POST",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: corpoDoEnsaio(arquivos),
            });
            if (!res.ok)
                throw new Error(`Falha ao baixar o pacote do ensaio (${res.status})`);
            return await res.blob();
        },
        /**
         * Histórico da troca de arquivos, paginado NO SERVIDOR.
         *
         * As outras listas do app baixam tudo e fatiam no cliente; aqui não dá — o
         * histórico ganha linhas todo mês, para sempre. `total` vem junto porque a
         * tela mostra "Mostrando de 1 até 10 de 143 registros".
         */
        trocaArquivos: (f = {}) => {
            const qs = new URLSearchParams();
            for (const [k, v] of Object.entries(f)) {
                if (v !== undefined && v !== "")
                    qs.set(k, String(v));
            }
            return this.request(`/v1/prefeitura/troca-arquivos?${qs}`);
        },
        /**
         * URL do arquivo QUE FOI TROCADO — o blob guardado, não uma regeração.
         *
         * Rota autenticada: a tela busca com token e monta o blob.
         */
        trocaArquivoUrl: (id) => `${this.opts.baseUrl}/v1/prefeitura/troca-arquivos/${encodeURIComponent(id)}/arquivo`,
        /**
         * Confere o retorno SEM importar.
         *
         * Não toca em contrato, parcela nem resultado. Serve à aba “Validar
         * arquivo” e ao passo de confirmação do envio normal — quem sobe vê quantas
         * linhas, quanto dinheiro e quantos problemas antes de decidir.
         */
        validarRetornoZip: async (folhaId, zip) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/prefeitura/folhas/${encodeURIComponent(folhaId)}/retorno/validar`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/zip",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: zip,
            });
            const corpo = (await res.json().catch(() => null));
            if (!res.ok) {
                const err = corpo
                    ?.error;
                // `details` VIAJA JUNTO. A recusa por reconhecimento so' e' util com os
                // dois lados (`esperado`/`encontrado`), e um `new Error(msg)` os
                // perderia — a tela cairia na mensagem generica que esta feature veio
                // remover. Ver `lerReconhecimentoRecusado`.
                const e = new Error(err?.message ?? `falha ao validar (${res.status})`);
                e.details = err?.details;
                e.status = res.status;
                throw e;
            }
            return corpo;
        },
        /**
         * Tira o arquivo do histórico. **Não desfaz o que ele aplicou** — a
         * resposta traz um `aviso` quando o registro excluído é um retorno já
         * processado, porque nesse caso a correção é reenviar a parte.
         */
        excluirArquivoTrocado: (id, motivo) => this.request(`/v1/prefeitura/troca-arquivos/${encodeURIComponent(id)}?motivo=${encodeURIComponent(motivo)}`, { method: "DELETE" }),
        /** Envia o pacote de retorno (ZIP com carga + consignados).
         *
         *  Faz fetch proprio em vez de usar `request`: o corpo e BINARIO, e o
         *  `request` so serializa JSON ou FormData. Mandar o zip como JSON custaria
         *  base64 (+33%) e um decode do outro lado sem ganho nenhum. */
        enviarRetornoZip: async (folhaId, zip, 
        /**
         * `parte` recorta o arquivo: o que ele NÃO menciona ele não afirma, e sem
         * isso a segunda remessa do mês marca a primeira como falha em folha.
         * `observacao` é o texto que distingue as partes no histórico.
         */
        /**
         * `confirmarDerivados` só vale a pena mandar depois de o operador ter
         * VISTO a lista: é a confirmação humana de FR-019, e o envio sem ela
         * responde 422 sem gravar nem aplicar nada.
         */
        opts = {}) => {
            const token = await this.storage.getAccess();
            const qs = new URLSearchParams();
            if (opts.parte)
                qs.set("parte", opts.parte);
            if (opts.observacao)
                qs.set("observacao", opts.observacao);
            if (opts.confirmarDerivados)
                qs.set("confirmarDerivados", "1");
            const url = new URL(`/v1/prefeitura/folhas/${encodeURIComponent(folhaId)}/retorno${qs.toString() ? `?${qs}` : ""}`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/zip",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: zip,
            });
            // 422 e recusa do pacote e vem COM corpo explicando o motivo — a tela
            // precisa dele, entao nao pode virar throw generico.
            const corpo = (await res.json().catch(() => null));
            if (!res.ok && res.status !== 422) {
                const msg = corpo && "error" in corpo ? corpo.error?.message : undefined;
                throw new Error(msg ?? `Falha ao enviar o retorno (${res.status})`);
            }
            // Modo MANUAL (202): arquivo recebido e guardado, descontos ainda NAO
            // aplicados. E' sucesso — nao pode virar "resposta inesperada".
            if (corpo && "recebido" in corpo)
                return corpo;
            // Modo AUTOMATICO (200) ou recusa do pacote (422): trazem `rejeitado`.
            if (corpo && "rejeitado" in corpo)
                return corpo;
            throw new Error("resposta inesperada do servidor ao enviar o retorno");
        },
        /**
         * O relatório de conversão ou de geração de UM registro do histórico, em JSON.
         *
         * `temRelatorio` do item diz se há o que abrir: registro anterior à feature
         * e recusa por reconhecimento não têm relatório, e o botão precisa saber
         * disso antes do clique.
         */
        trocaArquivoRelatorioUrl: (id) => `${this.opts.baseUrl}/v1/prefeitura/troca-arquivos/${encodeURIComponent(id)}/relatorio`,
        /**
         * O relatório COMPLETO daquele arquivo, para a tela renderizar.
         *
         * Não é o mesmo que a URL acima: aquela serve para baixar o JSON cru, esta
         * traz o objeto para o painel. O `tipo` do registro decide qual dos dois
         * formatos volta — conversão no retorno, geração no movimento.
         */
        trocaArquivoRelatorio: async (id) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/prefeitura/troca-arquivos/${encodeURIComponent(id)}/relatorio`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok)
                throw new Error(`Falha ao abrir o relatório (${res.status})`);
            return (await res.json());
        },
        /** Layouts tecnicos do ciclo. Rotas publicas (abertas via <a href>). */
        // Apontavam pra `folhas/remessa/layout-tecnico`, que a API deixou de servir
        // em 27/08/2026 quando a remessa virou MOVIMENTO — link morto que ninguem
        // chamava e por isso ninguem viu quebrar.
        movimentoLayoutTecnicoUrl: () => `${this.opts.baseUrl}/v1/prefeitura/folhas/movimento/layout-tecnico`,
        movimentoLayoutTecnicoMdUrl: () => `${this.opts.baseUrl}/v1/prefeitura/folhas/movimento/layout-tecnico.md`,
        retornoLayoutTecnicoUrl: () => `${this.opts.baseUrl}/v1/prefeitura/folhas/retorno/layout-tecnico`,
        retornoLayoutTecnicoMdUrl: () => `${this.opts.baseUrl}/v1/prefeitura/folhas/retorno/layout-tecnico.md`,
        // Convênios + config (passo 5)
        convenios: (opts) => this.request("/v1/prefeitura/convenios", { query: queryDaUnidade(opts) }),
        convenioConfig: (id, opts) => this.request(`/v1/prefeitura/convenios/${id}/config`, { query: queryDaUnidade(opts) }),
        salvarConvenioConfig: (id, body) => this.request(`/v1/prefeitura/convenios/${id}/config`, {
            method: "PUT",
            body,
        }),
        // Contratos + tombamento (passo 7)
        /**
         * Contratos averbados na folha desta prefeitura. Sem `limit`, devolve a
         * lista inteira (C1).
         *
         * Filtros e recorte entraram JUNTOS (04/09/2026), e a ordem nao era
         * negociavel: paginar no servidor com a busca no cliente faria a busca
         * procurar so' dentro da pagina — o operador digitaria um ADF que existe e
         * a tela diria que nao existe, sem erro nenhum.
         *
         * `facetas` usa a semantica `exceto` (FR-010): cada dimensao e' contada com
         * os OUTROS filtros aplicados, menos o dela propria, para o numero do chip
         * responder "quantos eu veria se clicasse aqui".
         *
         * `opts` carrega a unidade ativa (009). E' ortogonal ao recorte: sem ele uma
         * responsavel com varias atendidas listaria sempre a primeira.
         */
        contratos: (q, opts) => this.request("/v1/prefeitura/contratos", {
            query: queryDaUnidade(opts, q),
        }),
        /** Baixa o CCB (contrato assinado pelo banco, PDF do R2) de um contrato
         *  averbado. Backend valida que o contrato pertence a esta prefeitura. */
        baixarContratoCcb: async (adf) => {
            const token = await this.storage.getAccess();
            const url = new URL(`/v1/prefeitura/contratos/${encodeURIComponent(adf)}/ccb.pdf`, this.opts.baseUrl).toString();
            const res = await this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) {
                // Preserva o reason do backend pra UI diferenciar 'ccb_nao_anexado' vs
                // 'arquivo_nao_encontrado_no_r2' vs 'contrato_nao_e_desta_prefeitura'.
                let reason;
                try {
                    const body = (await res.json());
                    reason = body.error?.code;
                }
                catch {
                    /* ignore */
                }
                const err = new Error(`Falha ao baixar CCB (${res.status})`);
                err.status = res.status;
                err.reason = reason;
                throw err;
            }
            return await res.blob();
        },
        historicoPagamentosAdf: (adf, opts) => this.request(`/v1/prefeitura/adf/${encodeURIComponent(adf)}/historico-pagamentos`, { query: queryDaUnidade(opts) }),
        // ADF / descontos em folha (passo 8)
        adfCompetencias: (opts) => this.request("/v1/prefeitura/adf/competencias", { query: queryDaUnidade(opts) }),
        adf: (competencia, opts) => this.request("/v1/prefeitura/adf", {
            query: queryDaUnidade(opts, { competencia }),
        }),
        adfCsvUrl: (competencia, opts) => `${this.opts.baseUrl}/v1/prefeitura/adf/${competencia}/download.csv${sufixoDaUnidade(opts)}`,
        adfPdfUrl: (competencia, opts) => `${this.opts.baseUrl}/v1/prefeitura/adf/${competencia}/lote.pdf${sufixoDaUnidade(opts)}`,
        confirmarAdf: (ids) => this.request("/v1/prefeitura/adf/confirmar", { method: "POST", body: { ids } }),
        reportarFalhaAdf: (ids, motivo) => this.request("/v1/prefeitura/adf/falha", { method: "POST", body: { ids, motivo } }),
        // Relatórios (passo 9)
        relServidoresPorVinculo: (opts) => this.request("/v1/prefeitura/relatorios/servidores-por-vinculo", { query: queryDaUnidade(opts) }),
        relMargemMedia: (opts) => this.request("/v1/prefeitura/relatorios/margem-media", { query: queryDaUnidade(opts) }),
        relContratosPorBanco: (opts) => this.request("/v1/prefeitura/relatorios/contratos-por-banco", { query: queryDaUnidade(opts) }),
        // Anuência (passo 10)
        anuencia: (opts) => this.request("/v1/prefeitura/anuencia", { query: queryDaUnidade(opts) }),
        aceitarAnuencia: (aceitoPor, opts) => this.request("/v1/prefeitura/anuencia", {
            method: "POST",
            body: { aceito: true, aceitoPor },
            query: queryDaUnidade(opts),
        }),
        // Perfis + 2FA (passo 1)
        perfis: () => this.request("/v1/prefeitura/perfis"),
        salvarPerfil: (body) => this.request("/v1/prefeitura/perfis", { method: "POST", body }),
        perfilPresets: () => this.request("/v1/prefeitura/perfil-presets"),
        criarPerfilPreset: (body) => this.request("/v1/prefeitura/perfil-presets", {
            method: "POST",
            body,
        }),
        renomearPerfilPreset: (key, nome) => this.request(`/v1/prefeitura/perfil-presets/${encodeURIComponent(key)}`, { method: "PATCH", body: { nome } }),
        excluirPerfilPreset: (key) => this.request(`/v1/prefeitura/perfil-presets/${encodeURIComponent(key)}`, { method: "DELETE" }),
        excluirPerfil: (id) => this.request(`/v1/prefeitura/perfis/${id}`, { method: "DELETE" }),
        /** O ADMIN redefine a senha de um sub-usuario. A senha nasce TEMPORARIA — o
         *  dono e' obrigado a troca-la no proximo login. */
        redefinirSenhaPerfil: (id, body) => this.request(`/v1/prefeitura/perfis/${id}/redefinir-senha`, { method: "POST", body }),
        /**
         * O usuario LOGADO troca a propria senha ("Minha conta").
         *
         * Atende os dois logins da organizacao: sub-usuario e o login da propria
         * organizacao — o servidor decide pelo `prefeitura_perfil_id` do token. E' o
         * unico caminho que limpa a marca de senha temporaria.
         */
        trocarMinhaSenha: (senhaAtual, novaSenha) => this.request("/v1/prefeitura/conta/senha", {
            method: "POST",
            body: { senha_atual: senhaAtual, nova_senha: novaSenha },
        }),
        reativarPerfil: (id) => this.request(`/v1/prefeitura/perfis/${id}/reativar`, { method: "POST" }),
        rotate2fa: (id) => this.request(`/v1/prefeitura/perfis/${id}/2fa/rotate`, {
            method: "POST",
        }),
        disable2fa: (id) => this.request(`/v1/prefeitura/perfis/${id}/2fa/disable`, { method: "POST" }),
        comunicados: () => this.request("/v1/prefeitura/comunicados"),
    };
    // ============ Internal ============
    async request(path, options = {}) {
        const url = this.buildUrl(path, options.query);
        const headers = { Accept: "application/json" };
        if (!options.isFormData)
            headers["Content-Type"] = "application/json";
        if (!options.skipAuth) {
            const token = await this.storage.getAccess();
            if (token)
                headers.Authorization = `Bearer ${token}`;
        }
        // Idempotency-Key automatico em POSTs de mutacao. Se o cliente re-tenta
        // o mesmo request por flake de rede / duplo clique, backend devolve a
        // resposta cacheada (24h TTL) em vez de executar de novo. Passivo — se
        // o handler nao usa withIdempotency, nao muda nada; se usa, ganha
        // protecao automatica. Cliente pode sobrescrever passando o header
        // custom em opts.headers (nao implementado ainda, so pra referencia).
        const method = (options.method ?? "GET").toUpperCase();
        if (method === "POST" && !options.isFormData) {
            headers["Idempotency-Key"] = this.newIdempotencyKey();
        }
        const buildBody = () => {
            if (options.body === undefined)
                return undefined;
            if (options.isFormData)
                return options.body;
            return JSON.stringify(options.body);
        };
        // Retry simples pra network errors ou 5xx transientes: 1 tentativa inicial +
        // 2 retries com backoff (500ms, 1500ms). Mantem os MESMOS headers, incluindo
        // Idempotency-Key — backend detecta a duplicata e devolve resposta cacheada.
        // NAO retenta 4xx (erro do cliente — retentar nao vai corrigir).
        const doFetch = () => this.fetchImpl(url, {
            method: options.method ?? "GET",
            headers,
            body: buildBody(),
            signal: options.signal,
        });
        let res;
        let lastErr = null;
        const backoff = [0, 500, 1500];
        for (let attempt = 0; attempt < backoff.length; attempt++) {
            if (backoff[attempt])
                await new Promise((r) => setTimeout(r, backoff[attempt]));
            try {
                res = await doFetch();
                // 5xx transiente -> retry. 4xx -> nao retenta.
                if (res.status >= 500 && res.status < 600 && attempt < backoff.length - 1)
                    continue;
                // 429 e' a UNICA excecao entre os 4xx: e' transiente por definicao (a
                // janela do limite vira sozinha) e retentar E' a correcao. Sem isto, um
                // pico momentaneo de requests deixa a tela em estado de erro ate o
                // usuario recarregar — foi o que quebrou a demonstracao pro cliente em
                // 05/08/2026. Respeita Retry-After quando cabe na janela de backoff.
                if (res.status === 429 && attempt < backoff.length - 1) {
                    const espera = Number(res.headers.get("retry-after"));
                    if (Number.isFinite(espera) && espera > 0 && espera <= 3) {
                        await new Promise((r) => setTimeout(r, espera * 1000));
                    }
                    continue;
                }
                break;
            }
            catch (e) {
                lastErr = e;
                // Abort do usuario -> nao retenta.
                if (e.name === "AbortError")
                    throw e;
                if (attempt === backoff.length - 1)
                    throw e;
            }
        }
        if (!res)
            throw lastErr ?? new Error("network error");
        // NAO todo 401 e' sessao expirada: a API responde 401 tambem quando a SENHA DE
        // TRANSACAO digitada esta errada (ou nao foi cadastrada), com
        // `code: "senha_transacao_invalida"`. Tratar aquele caso como sessao vencida
        // fazia duas coisas erradas — reenviava o POST com a MESMA senha errada, e,
        // quando o refresh falhava, chamava onAuthFailure() e DESLOGAVA o servidor por
        // ter errado a senha. Aqui o 401 sobe intacto pra tela mostrar "senha
        // incorreta" e deixar tentar de novo. Ver Errors.senhaTransacao na API.
        if (res.status === 401 && !options.skipAuth && !(await ehRecusaDeSenhaDeTransacao(res))) {
            const ok = await this.tryRefresh();
            if (ok) {
                const token2 = await this.storage.getAccess();
                if (token2)
                    headers.Authorization = `Bearer ${token2}`;
                res = await this.fetchImpl(url, {
                    method: options.method ?? "GET",
                    headers,
                    body: buildBody(),
                    signal: options.signal,
                });
            }
            else {
                this.opts.onAuthFailure?.();
            }
        }
        if (!res.ok) {
            let code = "http_error";
            let message = `HTTP ${res.status}`;
            let details;
            try {
                const body = (await res.json());
                if (body.error) {
                    code = body.error.code;
                    message = body.error.message;
                    details = body.error.details;
                }
            }
            catch {
                // ignore parse error
            }
            throw new ApiHttpError(res.status, code, message, details);
        }
        if (res.status === 204)
            return undefined;
        return (await res.json());
    }
    /** Baixa um manual de uso com o token da sessão. Tenta o refresh uma vez no
     *  401, como `request`: o botão fica na sidebar a sessão inteira, e o access
     *  token vencido não pode virar "manual indisponível". */
    async baixarManual(path) {
        const url = new URL(path, this.opts.baseUrl).toString();
        const buscar = async () => {
            const token = await this.storage.getAccess();
            return this.fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        };
        let res = await buscar();
        if (res.status === 401 && (await this.tryRefresh()))
            res = await buscar();
        if (!res.ok)
            throw new ApiHttpError(res.status, "http_error", `Falha ao baixar o manual (${res.status})`);
        return await res.blob();
    }
    async tryRefresh() {
        if (this.refreshing) {
            await this.refreshing;
            return (await this.storage.getAccess()) !== null;
        }
        this.refreshing = (async () => {
            const refresh = await this.storage.getRefresh();
            if (!refresh)
                return;
            try {
                const res = await this.fetchImpl(this.buildUrl("/v1/auth/refresh"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ refresh_token: refresh }),
                });
                if (!res.ok) {
                    await this.storage.clear();
                    return;
                }
                const tok = (await res.json());
                await this.storage.set(tok);
            }
            catch {
                await this.storage.clear();
            }
        })();
        try {
            await this.refreshing;
        }
        finally {
            this.refreshing = null;
        }
        return (await this.storage.getAccess()) !== null;
    }
    buildUrl(path, query) {
        const url = new URL(path, this.opts.baseUrl);
        if (query) {
            for (const [k, v] of Object.entries(query)) {
                if (v === undefined || v === null)
                    continue;
                url.searchParams.set(k, String(v));
            }
        }
        return url.toString();
    }
    /** Gera uma chave de idempotencia unica por request (uuid v4). Enviada
     *  automaticamente no header 'Idempotency-Key' em todo POST. Se o request
     *  falhar por rede/timeout e o usuario re-tentar, o backend devolve a
     *  resposta cacheada (24h) em vez de duplicar a operacao. */
    newIdempotencyKey() {
        // crypto.randomUUID esta em Node 19+, Deno, browsers modernos, Workers.
        const c = globalThis.crypto;
        if (c?.randomUUID)
            return c.randomUUID();
        // Fallback pra ambientes sem crypto.randomUUID (raro): timestamp + random.
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
    }
}
/**
 * True quando um 401 e' recusa da SENHA DE TRANSACAO, nao sessao expirada.
 *
 * A API usa 401 pros dois casos (o status e' contrato publico de `/v1/external/*`
 * e nao podia mudar), e o que distingue e' `error.code`. Sem esta checagem, errar a
 * senha de transacao disparava o fluxo de renovacao de sessao: o request era
 * reenviado com a MESMA senha errada e, se o refresh falhasse, o usuario era
 * deslogado por ter digitado a senha errada.
 *
 * Le de um `clone()`: o corpo de uma Response e' stream de leitura unica, e
 * consumi-lo aqui deixaria o parse de erro logo abaixo sem nada pra ler.
 * Qualquer falha devolve false — mantem o comportamento antigo, que e' o lado
 * seguro pra errar.
 */
async function ehRecusaDeSenhaDeTransacao(res) {
    try {
        const body = (await res.clone().json());
        return body?.error?.code === "senha_transacao_invalida";
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=client.js.map